﻿import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { api, type GeneratedDispatchRow, type ListResult, type OwnerVoteAccessRow, type ResolutionDispatchLogRow, type ResolutionDocumentRow, type ResolutionFaciliooAttachmentRow, type ResolutionFaciliooFileTypeDebugRow, type ResolutionRecipientRow, type ResolutionTriggerNotificationLogRow, type ResolutionVoteLogRow, type ResolutionVoteRow } from "../app/api";
import { loadUiSettings } from "../app/ui-settings";
import { DeleteCodeConfirmModal } from "../components/common/DeleteCodeConfirmModal";
import { StartResolutionModal } from "../components/common/StartResolutionModal";
import { ShippingProcessPanel } from "../components/resolution/ShippingProcessPanel";
import { isActionMenuEventTarget, useActionMenuAutoClose } from "../hooks/useActionMenuAutoClose";

type TabKey = "stammdaten" | "beschlusstext" | "empfaenger" | "stimmen" | "versand" | "ergebnis" | "dokumente" | "facilioo_dokumente";
type VoteValue = ResolutionVoteRow["voteValue"];

type RecipientRow = ResolutionRecipientRow & { temporaryAnalog?: boolean };

const DIGITAL_MAIL_MAX_ATTEMPTS = 3;
const normalizeEmail = (value?: string) => String(value ?? "").trim().toLowerCase();
const isValidEmailLike = (value?: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? "").trim());
const getGeneratedDispatchRowActivityAt = (row: GeneratedDispatchRow) =>
  row.digitalMailSentAt || row.digitalMailAttemptedAt || row.digitalMailNdrDetectedAt || row.createdAt || "";
const isGeneratedDispatchRowForOwner = (row: GeneratedDispatchRow, ownerId?: string) => {
  const normalizedOwnerId = String(ownerId ?? "").trim();
  if (!normalizedOwnerId) return false;
  return String(row.eigentuemerId ?? "").trim() === normalizedOwnerId || String(row.ownerId ?? "").trim() === normalizedOwnerId;
};
const RECIPIENT_TABLE_ROW_HEIGHT = 44;
const RECIPIENT_TABLE_OVERSCAN = 10;
const VOTE_TABLE_ROW_HEIGHT = 72;
const VOTE_TABLE_OVERSCAN = 8;
const RECIPIENT_PAGE_SIZE = 100;
const RECIPIENT_BULK_PAGE_SIZE = 5000;
const RESULT_VOTE_ROW_COUNT_OPTIONS = [10, 25, 50, 100] as const;
const RESOLUTION_SSE_ENABLED = String(
  (globalThis as { __APP_RESOLUTION_SSE_ENABLED__?: string | boolean }).__APP_RESOLUTION_SSE_ENABLED__ ??
    (import.meta as { env?: Record<string, unknown> }).env?.VITE_RESOLUTION_SSE_ENABLED ??
    "true"
)
  .trim()
  .toLowerCase() === "true";


type StartConfirmState = {
  beschlussnummer: string;
  endedatum: string;
  error: string;
} | null;
type StopConfirmState = {
  resolutionId: string;
  beschlussnummer: string;
  code: string;
  input: string[];
  error: string;
} | null;

type DocumentDeleteConfirmState = {
  documentId: string;
  documentName: string;
  code: string;
  input: string[];
  error: string;
} | null;


type VoteChangeConfirmState = {
  assignmentId: string;
  eigentuemerLabel: string;
  code: string;
  input: string[];
  error: string;
  voteValue: VoteValue;
  comment?: string;
} | null;

const tabs: Array<{ key: TabKey; label: string }> = [
  { key: "stammdaten", label: "Übersicht" },
  { key: "beschlusstext", label: "Beschluss" },
  { key: "empfaenger", label: "Empfänger" },
  { key: "stimmen", label: "Abstimmung" },
  { key: "versand", label: "Versandprozess" },
  { key: "ergebnis", label: "Ergebnis" },
  { key: "dokumente", label: "Dokumente" },
  { key: "facilioo_dokumente", label: "Facilioo-Dokumente" }
];

const getTodayDateKey = () => new Date().toISOString().slice(0, 10);

const formatDateDE = (value?: string) => {
  if (!value) return "-";
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : value;
};

const formatDateTimeDE = (value?: string) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(d);
};

const MOJIBAKE_PATTERN = /(Ã.|Â.|â.|ð.|�)/;

const normalizeMojibake = (value?: string) => {
  const raw = String(value ?? "");
  if (!raw) return "";
  let next = raw;
  for (let i = 0; i < 3; i += 1) {
    if (!MOJIBAKE_PATTERN.test(next)) break;
    try {
      const bytes = Uint8Array.from(next, (char) => char.charCodeAt(0) & 0xff);
      const decoded = new TextDecoder("utf-8").decode(bytes);
      if (!decoded || decoded === next) break;
      next = decoded;
    } catch {
      break;
    }
  }
  return next
    .replace(/Eigent(?:Ã¼|Ãœ|ÃƒÂ¼|[ՈՕ]¼|¼)mer/g, "Eigentümer")
    .replace(/Stimm(?:Ã¤|Ã„|ÃƒÂ¤|[ՈՕ]¤|¤)nderung/g, "Stimmänderung")
    .replace(/ung(?:Ã¼|Ãœ|ÃƒÂ¼|[ՈՕ]¼|¼)ltig/g, "ungültig");
};

const formatFileSize = (sizeBytes: number) => {
  if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) return "-";
  if (sizeBytes < 1024) return `${Math.round(sizeBytes)} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(2)} MB`;
};

const resolveFileTypeLabel = (row: ResolutionFaciliooAttachmentRow) => {
  const fromFaciliooType = String(row.fileTypeLabel ?? "").trim();
  if (fromFaciliooType) return fromFaciliooType;
  const fromFaciliooExtension = String(row.fileExtension ?? "").trim();
  if (fromFaciliooExtension) return fromFaciliooExtension.toUpperCase();
  const name = String(row.originalFilename || row.internalFilename || "").trim().toLowerCase();
  const dotIndex = name.lastIndexOf(".");
  if (dotIndex >= 0 && dotIndex < name.length - 1) {
    return name.slice(dotIndex + 1).toUpperCase();
  }
  if (Number.isFinite(Number(row.typeId))) return `Typ ${Number(row.typeId)}`;
  return "-";
};

const resolveAttachmentFilename = (row: ResolutionFaciliooAttachmentRow) => {
  const base = String(row.originalFilename || row.internalFilename || "").trim() || `Anhang_${row.attachmentId}`;
  const ext = String(row.fileExtension ?? "").trim().replace(/^\./, "");
  if (!ext) return base;
  if (new RegExp(`\\.${ext}$`, "i").test(base)) return base;
  return `${base}.${ext}`;
};

const isPreviewableFaciliooAttachment = (row: ResolutionFaciliooAttachmentRow) => {
  const mime = String(row.mimeType ?? "").trim().toLowerCase();
  if (mime === "application/pdf" || mime.startsWith("image/")) return true;
  const extFromType = String(row.fileExtension ?? "").trim().toLowerCase().replace(/^\./, "");
  if (["pdf", "png", "jpg", "jpeg", "gif", "webp", "svg"].includes(extFromType)) return true;
  const resolvedName = resolveAttachmentFilename(row).toLowerCase();
  return [".pdf", ".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg"].some((suffix) => resolvedName.endsWith(suffix));
};

const formatStatus = (status: "entwurf" | "aktiv" | "abgeschlossen") => {
  if (status === "aktiv") return "läuft";
  if (status === "abgeschlossen") return "beendet";
  return "Entwurf";
};

const formatQuorum = (value: "einfache_mehrheit" | "qualifizierte_mehrheit" | "allstimmig") => {
  if (value === "qualifizierte_mehrheit") return "Qualifizierte Mehrheit";
  if (value === "allstimmig") return "Allstimmig";
  return "Einfache Mehrheit";
};

const formatStimmrechtsart = (value: "kopf" | "stimmrecht" | "mea") => {
  if (value === "kopf") return "Kopfstimmen";
  if (value === "mea") return "MEA";
  return "Stimmrecht";
};

const formatTriggerDeliveryStatus = (status?: string) => {
  const normalized = String(status ?? "").trim().toLowerCase();
  if (normalized === "sent" || normalized === "delivered") return "Versendet";
  if (normalized === "failed") return "Fehlgeschlagen";
  if (normalized === "queued" || normalized === "pending") return "In Warteschlange";
  if (normalized === "retrying") return "Wiederholung läuft";
  if (normalized === "processing") return "In Bearbeitung";
  if (normalized === "skipped") return "Übersprungen";
  if (normalized === "cancelled" || normalized === "canceled") return "Abgebrochen";
  if (!normalized) return "-";
  return status ?? "-";
};

const formatTriggerEventType = (eventType?: string) => {
  const normalized = String(eventType ?? "").trim().toLowerCase();
  if (normalized === "resolution_started") return "Beschluss gestartet";
  if (normalized === "invitation_sent_success") return "Einladung erfolgreich versendet";
  if (normalized === "reminder_sent") return "Erinnerung versendet";
  if (normalized === "each_vote_submitted") return "Stimmabgabe eingegangen";
  if (normalized === "vote_with_comment") return "Stimmabgabe mit Kommentar";
  if (normalized === "preliminary_result_locked") return "Vorläufiges Endergebnis erreicht";
  if (normalized === "resolution_finished_with_result") return "Beschluss beendet";
  if (normalized === "email_bounce_after_retries") return "E-Mail-Rückläufer nach Retries";
  if (normalized === "analog_postal_action_required") return "Analoger Versand erforderlich";
  if (normalized === "delivery_retries_exhausted") return "E-Mail-Rückläufer nach Retries";
  if (normalized === "analog_dispatch_required") return "Analoger Versand erforderlich";
  if (!normalized) return "-";
  return eventType ?? "-";
};

const formatTriggerChannel = (channel?: string) => {
  const normalized = String(channel ?? "").trim().toLowerCase();
  if (normalized === "email") return "E-Mail";
  if (normalized === "facilioo") return "Facilioo";
  if (!normalized) return "-";
  return channel ?? "-";
};

const getRemainingDaysForActiveResolution = (status: "entwurf" | "aktiv" | "abgeschlossen", endedatum?: string): string => {
  if (!endedatum) return "-";

  const match = endedatum.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "-";

  const end = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const diffMs = end.getTime() - today.getTime();
  if (status !== "aktiv") return "0";

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
  if (days <= 0) return "0";
  return String(days);
};

const formatVoteValue = (value: VoteValue) => {
  if (value === "ja") return "Ja";
  if (value === "nein") return "Nein";
  if (value === "enthaltung") return "Enthaltung";
  return "Offen";
};

const formatUnitNumberForResult = (value?: string) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "-";
  if (/^\d+$/.test(raw)) return raw.padStart(4, "0");
  return raw;
};

const formatVoteEventType = (value?: string) => {
  const normalized = normalizeMojibake(value);
  if (normalized === "stimmänderung") return "Stimmänderung";
  if (normalized === "erstmalige_stimmabgabe") return "Erstabgabe";
  return normalized || "-";
};

const formatSubmissionChannel = (value?: string) => {
  const normalized = normalizeMojibake(value);
  if (normalized === "admin_console") return "Admin-Konsole";
  if (normalized === "owner_link") return "Eigentümer-Link";
  if (normalized === "owner_login") return "Eigentümer-Login";
  if (normalized === "system_dispatch") return "Systemversand";
  return normalized || "-";
};

const ResolutionKpiIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M7.1 4.6h6.6l3.2 3.2v11.6H7.1z" stroke="currentColor" strokeWidth="1.85" strokeLinejoin="round" />
    <path d="M13.7 4.8v3.1h3" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" />
    <path d="m9.4 14.1 2 2 4.2-5" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="17.7" cy="17.8" r="2.1" fill="#fff" stroke="currentColor" strokeWidth="1.85" />
  </svg>
);

const RemainingTimeIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="11" r="5.8" stroke="currentColor" strokeWidth="1.7" />
    <path d="M10 11V7.8M10 11l2.5 1.8M8 3.8h4M9 3.8v1.8M11 3.8v1.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const OwnerNameIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="9.1" cy="9.1" r="2.5" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="15.8" cy="8.4" r="2.1" stroke="currentColor" strokeWidth="1.8" />
    <path d="M4.7 18.6a5 5 0 0 1 8.8 0M12.2 18.6a4.2 4.2 0 0 1 7.1 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const ReachabilityIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="5.4" y="2.8" width="9.2" height="14.4" rx="1.9" stroke="currentColor" strokeWidth="1.7" />
    <path d="M8.1 5.8h3.8M7.4 14.1l5.2-5.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const ReturnRateIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M15.2 7.2a5.8 5.8 0 1 0 .4 6.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <path d="m14 4.7 2.3 2.5-2.5 2.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ResultIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M6.2 4.8h7.6v1.3a3.8 3.8 0 0 1-7.6 0V4.8Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    <path d="M8.2 10.2h3.6M10 10.2v2.6M7.6 15h4.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M6.2 5.6H4.6A1.8 1.8 0 0 0 2.8 7.4v.2a2.6 2.6 0 0 0 2.6 2.6h.5M13.8 5.6h1.6a1.8 1.8 0 0 1 1.8 1.8v.2a2.6 2.6 0 0 1-2.6 2.6h-.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ResultOutcomeIcon = ({ rejected }: { rejected: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {rejected ? (
      <path d="M7 7 17 17M17 7 7 17" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    ) : (
      <path d="m5 12 4.2 4.2L19 6.6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    )}
  </svg>
);

const QuorumOutcomeIcon = ({ fulfilled }: { fulfilled: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {fulfilled ? (
      <>
        <path d="M7.2 10.2h-2a1.4 1.4 0 0 0-1.4 1.4v6.5a1.4 1.4 0 0 0 1.4 1.4h2V10.2Z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
        <path d="M7.2 10.2l4.1-6.1c.8-1.2 2.7-.6 2.7.9v3.1h4.4a2 2 0 0 1 1.9 2.5l-1.5 6.7a2.8 2.8 0 0 1-2.7 2.2H7.2" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ) : (
      <>
        <path d="M7.2 13.8h-2a1.4 1.4 0 0 1-1.4-1.4V5.9a1.4 1.4 0 0 1 1.4-1.4h2v9.3Z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
        <path d="M7.2 13.8l4.1 6.1c.8 1.2 2.7.6 2.7-.9v-3.1h4.4a2 2 0 0 0 1.9-2.5l-1.5-6.7a2.8 2.8 0 0 0-2.7-2.2H7.2" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
      </>
    )}
  </svg>
);

const ResolutionDetailTableIcon = ({ name }: { name: "search" | "owner" | "ownerName" | "mail" | "key" | "link" | "status" | "unit" | "text" | "home" | "pin" | "reachability" | "vote" | "date" | "comment" | "action" | "select" | "document" | "size" | "type" }) => {
  if (name === "search") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="9" cy="9" r="5.8" stroke="currentColor" strokeWidth="1.8" />
        <path d="m13.2 13.2 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "owner") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="10" cy="7.4" r="2.5" stroke="currentColor" strokeWidth="1.7" />
        <path d="M5.2 15.9a4.8 4.8 0 0 1 9.6 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "ownerName") return <OwnerNameIcon />;
  if (name === "reachability") return <ReachabilityIcon />;
  if (name === "vote") return <ResultIcon />;
  if (name === "date") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="4.2" y="5.1" width="11.6" height="10.7" rx="1.8" stroke="currentColor" strokeWidth="1.7" />
        <path d="M7.2 3.8v3M12.8 3.8v3M4.8 8.4h10.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "comment") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M4.5 5.1h11v7.1a1.7 1.7 0 0 1-1.7 1.7H8.5l-3.2 2.4v-2.4h-.8a1.7 1.7 0 0 1-1.7-1.7V6.8a1.7 1.7 0 0 1 1.7-1.7Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M6.4 8.2h7.1M6.4 11h4.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "action") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="10" cy="4.8" r="1.3" fill="currentColor" />
        <circle cx="10" cy="10" r="1.3" fill="currentColor" />
        <circle cx="10" cy="15.2" r="1.3" fill="currentColor" />
      </svg>
    );
  }
  if (name === "select") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="4.1" y="4.1" width="11.8" height="11.8" rx="2.1" stroke="currentColor" strokeWidth="1.7" />
        <path d="m7.2 10.1 2 2 3.9-4.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "size") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M4.5 14.5h11M5.2 11.2h9.6M6 7.9h8M6.8 4.6h6.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "type") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M5.1 4.2h9.8v11.6H5.1z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M7.4 7.3h5.2M7.4 10h5.2M7.4 12.7h3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "mail") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="3.8" y="5.2" width="12.4" height="9.6" rx="1.6" stroke="currentColor" strokeWidth="1.7" />
        <path d="m4.5 6 5.5 4.1L15.5 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "key") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="7.2" cy="10.1" r="3.1" stroke="currentColor" strokeWidth="1.7" />
        <path d="M10.1 10.1h6M14 10.1v2M16 10.1v2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "link") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M8.3 11.7a3.1 3.1 0 0 0 4.4 0l2.2-2.2a3.1 3.1 0 0 0-4.4-4.4L9.2 6.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <path d="M11.7 8.3a3.1 3.1 0 0 0-4.4 0L5.1 10.5a3.1 3.1 0 0 0 4.4 4.4l1.3-1.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "status") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="10" cy="10" r="5.9" stroke="currentColor" strokeWidth="1.7" />
        <path d="m10 7.2 1.8 2.5L10 12.8 8.2 9.7 10 7.2Z" fill="currentColor" />
      </svg>
    );
  }
  if (name === "unit") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="5.1" y="4.1" width="9.8" height="11.8" rx="1.8" stroke="currentColor" strokeWidth="1.7" />
        <path d="M7.4 7.5h2M10.8 7.5h2M7.4 10.5h2M10.8 10.5h2M8.1 15.9v-2.2h3.8v2.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "home") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M4.4 8.7 10 4.4l5.6 4.3v6.8h-4.1v-3.8h-3v3.8H4.4V8.7Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "pin") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M10 17s4.8-4.6 4.8-8.1a4.8 4.8 0 1 0-9.6 0C5.2 12.4 10 17 10 17Z" stroke="currentColor" strokeWidth="1.7" />
        <circle cx="10" cy="8.9" r="1.6" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M5.2 3.8h6.5l2.9 2.9v8.6a1.2 1.2 0 0 1-1.2 1.2H5.2A1.2 1.2 0 0 1 4 15.3V5a1.2 1.2 0 0 1 1.2-1.2Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M6.8 9.4h6.1M6.8 12.2h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
};

const ResolutionDetailTableHeader = ({ label, icon, center = false }: { label: string; icon: Parameters<typeof ResolutionDetailTableIcon>[0]["name"]; center?: boolean }) => (
  <span className={`resolutions-v2-th-plain${center ? " resolution-detail-v2-th-center" : ""}`}>
    <span className="resolutions-v2-th-icon" aria-hidden="true"><ResolutionDetailTableIcon name={icon} /></span>
    <span className="resolutions-v2-th-label">{label}</span>
  </span>
);

const formatAuthMode = (value?: string, authUserName?: string) => {
  const normalized = normalizeMojibake(value);
  const normalizedUserName = normalizeMojibake(authUserName);
  if (normalized === "admin_session") return normalizedUserName ? `Admin-Session (${normalizedUserName})` : "Admin-Session";
  if (normalized === "beschluss_link") return "Beschluss-Link";
  if (normalized === "beschluss_id_passwort") return "Beschluss-ID + Passwort";
  return normalized || "-";
};
const formatAuthUser = (name?: string, id?: string) => {
  const normalizedName = normalizeMojibake(name).trim();
  const normalizedId = normalizeMojibake(id).trim();
  if (normalizedName && normalizedId) return `${normalizedName} (${normalizedId})`;
  if (normalizedName) return normalizedName;
  if (normalizedId) return normalizedId;
  return "-";
};
const formatShare = (value: number, total: number) => {
  if (total <= 0) return "0,0";
  return ((value / total) * 100).toFixed(1).replace(".", ",");
};
const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (globalThis.navigator?.clipboard?.writeText && globalThis.isSecureContext) {
      await globalThis.navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fallback below
  }

  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const createCommandIdempotencyKey = (prefix: "start" | "stop") =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const formatDispatchType = (value: string) => {
  if (value === "einladung_versendet") return "Einladung zur Abstimmung";
  if (value === "erinnerung_1_versendet") return "1. Erinnerung";
  if (value === "erinnerung_2_versendet") return "2. Erinnerung";
  if (value === "beschlussergebnis_versendet") return "Beschlussergebnis";
  return value;
};

const mergeDispatchLogRows = (existing: ResolutionDispatchLogRow[], incoming: ResolutionDispatchLogRow[]) => {
  const byId = new Map<string, ResolutionDispatchLogRow>();
  for (const row of existing) byId.set(row.id, row);
  for (const row of incoming) byId.set(row.id, row);
  return [...byId.values()].sort((a, b) => b.timestamp.localeCompare(a.timestamp));
};

const dispatchEventOrder = [
  "einladung_versendet",
  "erinnerung_1_versendet",
  "erinnerung_2_versendet",
  "beschlussergebnis_versendet"
] as const;

type DispatchUiStatus = "versendet" | "fehlgeschlagen" | "uebersprungen" | "vorgemerkt" | "abgestimmt" | "ausstehend";

const formatDispatchUiStatus = (status: DispatchUiStatus) => {
  if (status === "versendet") return "Versendet";
  if (status === "fehlgeschlagen") return "Fehlgeschlagen";
  if (status === "uebersprungen") return "Übersprungen";
  if (status === "vorgemerkt") return "Vorgemerkt";
  if (status === "abgestimmt") return "Abgestimmt";
  return "Ausstehend";
};

const getDispatchUiStatusBadgeClass = (status: DispatchUiStatus) => {
  if (status === "versendet") return "aktiv";
  if (status === "ausstehend" || status === "fehlgeschlagen") return "inaktiv";
  return "keine_zuordnung";
};

const isPreviewableDocument = (doc: ResolutionDocumentRow) => {
  const mime = (doc.mimeType ?? "").toLowerCase();
  return mime === "application/pdf" || mime.startsWith("image/");
};
const buildVoteLogCsv = (rows: ResolutionVoteLogRow[], resolveOwnerName: (ownerId?: string | null) => string) => {
  const header = ["Zeitpunkt", "Ereignis", "IP", "Kanal", "Authentifizierung", "Authentifiziert durch", "Eigentümer", "Stimme", "Geändert"];
  const body = rows.map((log) => {
    const changed = Array.isArray(log.changed_assignments) ? log.changed_assignments : [];
    const changedLabel = changed.length
      ? changed.map((c) => `${c.previousVoteValue ?? "offen"} -> ${c.nextVoteValue ?? "offen"}`).join(", ")
      : "-";

    return [
      log.timestamp ? formatDateTimeDE(log.timestamp) : "-",
      formatVoteEventType(log.vote_event_type),
      normalizeMojibake(String(log.client_ip ?? "-")),
      formatSubmissionChannel(log.submission_channel),
      formatAuthMode(log.auth_mode, log.auth_user_name ?? log.actor_name),
      formatAuthUser(log.auth_user_name, log.auth_user_id),
      resolveOwnerName(log.owner_id),
      log.vote_value ? formatVoteValue(log.vote_value) : "-",
      normalizeMojibake(changedLabel)
    ];
  });

  const escapeCsv = (value: string) => `"${value.replace(/"/g, '""')}"`;
  return [header, ...body].map((row) => row.map((cell) => escapeCsv(String(cell))).join(";")).join("\n");
};

const outcomeBadge = (status: "entwurf" | "aktiv" | "abgeschlossen", quorumErfuellt?: boolean) => {
  if (status !== "abgeschlossen") return <span className="badge ergebnis-neutral">-</span>;
  if (quorumErfuellt) return <span className="badge ergebnis-angenommen">Angenommen</span>;
  return <span className="badge ergebnis-abgelehnt">Abgelehnt</span>;
};

const VoteDonutChart = ({ ja, nein, enthaltung }: { ja: number; nein: number; enthaltung: number }) => {
  const bewerteteSumme = Math.max(ja + nein, 0);
  const gesamtMitEnthaltung = Math.max(ja + nein + enthaltung, 0);
  const jaPct = bewerteteSumme > 0 ? (ja / bewerteteSumme) * 100 : 0;
  const neinPct = bewerteteSumme > 0 ? (nein / bewerteteSumme) * 100 : 0;
  const yesColor = "color-mix(in srgb, color-mix(in srgb, #eefaf3 90%, #1f8a51 10%) 95%, transparent 5%)";
  const noColor = "color-mix(in srgb, color-mix(in srgb, #fff7f7 90%, #d34848 10%) 95%, transparent 5%)";
  const abstainColor = "color-mix(in srgb, color-mix(in srgb, #fff8df 86%, #d99a00 14%) 95%, transparent 5%)";

  const jaSegment = gesamtMitEnthaltung > 0 ? (ja / gesamtMitEnthaltung) * 100 : 0;
  const neinSegment = gesamtMitEnthaltung > 0 ? (nein / gesamtMitEnthaltung) * 100 : 0;
  const p1 = jaSegment;
  const p2 = jaSegment + neinSegment;
  const gradient =
    gesamtMitEnthaltung > 0
      ? `conic-gradient(${yesColor} 0% ${p1}%, ${noColor} ${p1}% ${p2}%, ${abstainColor} ${p2}% 100%)`
      : "conic-gradient(#e5e9ee 0% 100%)";

  return (
    <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
      <div
        className="result-vote-donut-v2"
        aria-label="Stimmen Kreisdiagramm"
        title="Stimmenverteilung"
        style={{
          width: 170,
          height: 170,
          borderRadius: "50%",
          background: gradient,
          position: "relative"
        }}
      >
        <div
          className="result-vote-donut-inner-v2"
          style={{
            position: "absolute",
            inset: 42,
            borderRadius: "50%",
            background: "var(--surface)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            color: "var(--text-soft)",
            textAlign: "center",
            padding: 6,
            whiteSpace: "pre-line"
          }}
        >
          {gesamtMitEnthaltung > 0 ? `${gesamtMitEnthaltung}` + "\nStimmen" : "Keine\nStimmen"}
        </div>
      </div>
      <div style={{ display: "grid", gap: 6, minWidth: 190 }}>
        <div style={{ display: "grid", gridTemplateColumns: "120px auto", alignItems: "center", columnGap: 10 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: yesColor }} />Ja</span>
          <strong>{ja.toFixed(2)} ({jaPct.toFixed(1)} %)</strong>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "120px auto", alignItems: "center", columnGap: 10 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: noColor }} />Nein</span>
          <strong>{nein.toFixed(2)} ({neinPct.toFixed(1)} %)</strong>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "120px auto", alignItems: "center", columnGap: 10 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: abstainColor }} />Enthaltung</span>
          <strong>{enthaltung.toFixed(2)} (ohne Bewertung)</strong>
        </div>
      </div>
    </div>
  );
};

const getReachabilityBadge = (erreichbarkeit: "digital" | "analog", temporaryAnalog?: boolean) => {
  if (temporaryAnalog) {
    return { label: "temporär analog", className: "erreichbar-temp-analog" };
  }
  if (erreichbarkeit === "analog") {
    return { label: "analog", className: "erreichbar-analog" };
  }
  return { label: "digital", className: "erreichbar-digital" };
};

type ResolutionDetailPageProps = {
  canManageMasterDataActions?: boolean;
};

export const ResolutionDetailPage = ({ canManageMasterDataActions = false }: ResolutionDetailPageProps) => {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabKey>("stammdaten");
  const [searchEmpfaenger, setSearchEmpfaenger] = useState("");
  const [searchStimmen, setSearchStimmen] = useState("");
  const [filterStimmenErreichbarkeit, setFilterStimmenErreichbarkeit] = useState<"alle" | "digital" | "analog">("alle");
  const [filterStimmenverhalten, setFilterStimmenverhalten] = useState<"alle" | VoteValue>("alle");
  const [searchVersand, setSearchVersand] = useState("");
  const [searchErgebnis, setSearchErgebnis] = useState("");
  const [searchZugangsdaten, setSearchZugangsdaten] = useState("");
  const [searchDokumente, setSearchDokumente] = useState("");
  const [searchFaciliooDokumente, setSearchFaciliooDokumente] = useState("");
  const [isResultVotesModalOpen, setIsResultVotesModalOpen] = useState(false);
  const [resultVotesRowCount, setResultVotesRowCount] = useState<number | "all">(25);
  const [empfaengerPage, setEmpfaengerPage] = useState(1);
  const [stimmenPage, setStimmenPage] = useState(1);
  const [actionError, setActionError] = useState("");
  const [draftVotes, setDraftVotes] = useState<Record<string, VoteValue>>({});
  const [draftComments, setDraftComments] = useState<Record<string, string>>({});
  const [voteChangeConfirm, setVoteChangeConfirm] = useState<VoteChangeConfirmState>(null);
  const [startConfirm, setStartConfirm] = useState<StartConfirmState>(null);
  const [stopConfirm, setStopConfirm] = useState<StopConfirmState>(null);
  const [documentDeleteConfirm, setDocumentDeleteConfirm] = useState<DocumentDeleteConfirmState>(null);
  const [isVoteLogModalOpen, setIsVoteLogModalOpen] = useState(false);
  const [voteOpenActionMenuId, setVoteOpenActionMenuId] = useState<string | null>(null);
  const [isVoteActionMenuHovered, setIsVoteActionMenuHovered] = useState(false);
  const [documentOpenActionMenuId, setDocumentOpenActionMenuId] = useState<string | null>(null);
  const [isDocumentActionMenuHovered, setIsDocumentActionMenuHovered] = useState(false);
  const [faciliooOpenActionMenuId, setFaciliooOpenActionMenuId] = useState<string | null>(null);
  const [isFaciliooActionMenuHovered, setIsFaciliooActionMenuHovered] = useState(false);
  const [actionMenuPosition, setActionMenuPosition] = useState<{ left: number; top: number } | null>(null);
  const [copiedDirectLinkId, setCopiedDirectLinkId] = useState<string | null>(null);
  const [ownerEinzelstimmabgabeSichtbar, setOwnerEinzelstimmabgabeSichtbar] = useState(false);
  const [documentFiles, setDocumentFiles] = useState<File[]>([]);
  const [documentError, setDocumentError] = useState("");
  const [documentInfo, setDocumentInfo] = useState("");
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [faciliooDocumentError, setFaciliooDocumentError] = useState("");
  const [showFileTypesDebug, setShowFileTypesDebug] = useState(false);
  const [selectedFaciliooAttachmentKeys, setSelectedFaciliooAttachmentKeys] = useState<string[]>([]);
  const [previewDocument, setPreviewDocument] = useState<ResolutionDocumentRow | null>(null);
  const [previewFaciliooDocument, setPreviewFaciliooDocument] = useState<{ name: string; url: string; previewable: boolean } | null>(null);
  const [documentDropActive, setDocumentDropActive] = useState(false);
  const documentInputRef = useRef<HTMLInputElement | null>(null);
  const READ_STALE_TIME_MS = 20_000;
  const LOG_STALE_TIME_MS = 10_000;

  const resolutionQuery = useQuery({
    queryKey: ["resolution", id],
    queryFn: () => api.resolutionById(id as string),
    enabled: !!id,
    staleTime: READ_STALE_TIME_MS,
    placeholderData: (previousData) => previousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const resolution = resolutionQuery.data;
  const quorumKpiHinweis = useMemo(() => {
    if (!resolution) return "";
    if (resolution.quorum === "allstimmig") return "";
    if (!resolution.vorbeschluss_top || !resolution.vorbeschluss_wev_datum) return "";
    return `Abweichendes Quorum gegenüber Allstimmigkeit auf Basis des Vorbeschlusses unter TOP ${resolution.vorbeschluss_top} der WEV vom ${formatDateDE(resolution.vorbeschluss_wev_datum)}.`;
  }, [resolution]);

  useEffect(() => {
    setOwnerEinzelstimmabgabeSichtbar(Boolean(resolution?.owner_einzelstimmabgabe_sichtbar));
  }, [resolution?.owner_einzelstimmabgabe_sichtbar]);

  const resultQuery = useQuery({
    queryKey: ["resolution-result", id],
    queryFn: () => api.resolutionResult(id as string),
    enabled: !!id && (tab === "stimmen" || tab === "ergebnis"),
    staleTime: READ_STALE_TIME_MS,
    placeholderData: (previousData) => previousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });
  const kpiQuery = useQuery({
    queryKey: ["resolution-kpis", id],
    queryFn: () => api.resolutionKpis(id as string),
    enabled: !!id && (tab === "stammdaten" || tab === "stimmen" || tab === "ergebnis"),
    staleTime: READ_STALE_TIME_MS,
    placeholderData: (previousData) => previousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });
  const expectedResolutionUpdatedAt = kpiQuery.data?.updatedAt ?? resolution?.updatedAt;
  const resultKpiTitle = useMemo(() => {
    if (resolution?.status === "abgeschlossen") return "Endergebnis";
    if (resultQuery.data?.break_even?.isLocked || kpiQuery.data?.break_even?.isLocked) return "Vorläufiges Endergebnis";
    return "Laufende Stimmauswertung";
  }, [kpiQuery.data?.break_even?.isLocked, resolution?.status, resultQuery.data?.break_even?.isLocked]);
  const resultKpiValue = useMemo(() => {
    const raw = resultQuery.data?.ergebnis ?? kpiQuery.data?.ergebnis ?? "-";
    if ((resolution?.status === "abgeschlossen" || resultQuery.data?.break_even?.isLocked || kpiQuery.data?.break_even?.isLocked) && /^vorläufig\s+/i.test(raw)) {
      return raw.replace(/^vorläufig\s+/i, "");
    }
    return raw;
  }, [kpiQuery.data?.break_even?.isLocked, kpiQuery.data?.ergebnis, resolution?.status, resultQuery.data?.break_even?.isLocked, resultQuery.data?.ergebnis]);
  const showResultBreakEvenNote =
    resolution?.status === "aktiv" &&
    Boolean(resultQuery.data?.break_even?.isLocked || kpiQuery.data?.break_even?.isLocked);
  const resultKpiValueText = String(resultKpiValue ?? "").toLowerCase();
  const resultKpiTone = resultKpiValueText.includes("angenommen")
    ? "ok"
    : resultKpiValueText.includes("abgelehnt")
    ? "fail"
    : "neutral";
  const resultQuorumFulfilled = Boolean(kpiQuery.data?.quorumErfuellt ?? resultQuery.data?.quorumErfuellt);
  const resultReturnSubmitted = Number.isFinite(Number(resultQuery.data?.stimmen.abgegeben))
    ? Number(resultQuery.data?.stimmen.abgegeben ?? 0)
    : 0;
  const resultReturnOpen = Number.isFinite(Number(resultQuery.data?.stimmen.offen))
    ? Number(resultQuery.data?.stimmen.offen ?? 0)
    : 0;
  const resultReturnTotal = Number.isFinite(Number(resultQuery.data?.stimmen.gesamt))
    ? Number(resultQuery.data?.stimmen.gesamt ?? 0)
    : Math.max(resultReturnSubmitted + resultReturnOpen, 0);
  const resultReturnSubmittedPct = resultReturnTotal > 0 ? (resultReturnSubmitted / resultReturnTotal) * 100 : 0;
  const resultReturnOpenPct = resultReturnTotal > 0 ? (resultReturnOpen / resultReturnTotal) * 100 : 0;
  const resultReturnBarStyle = {
    "--result-return-submitted": `${Math.max(0, Math.min(100, resultReturnSubmittedPct))}%`,
    "--result-return-open": `${Math.max(0, Math.min(100, resultReturnOpenPct))}%`
  } as CSSProperties;

  const voteLogQuery = useQuery({
    queryKey: ["resolution-vote-log", id],
    queryFn: () => api.resolutionVoteLog(id as string),
    enabled: !!id && tab === "stimmen",
    staleTime: LOG_STALE_TIME_MS,
    placeholderData: (previousData) => previousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const needsRecipientDataset =
    tab === "empfaenger" ||
    tab === "stimmen" ||
    tab === "versand" ||
    tab === "ergebnis";

  const dispatchLogQuery = useQuery({
    queryKey: ["resolution-dispatch-log", id],
    queryFn: async () => {
      const queryKey = ["resolution-dispatch-log", id] as const;
      const previous = qc.getQueryData<{ data: ResolutionDispatchLogRow[] }>(queryKey);
      const cursor = previous?.data?.[0]?.timestamp;
      const response = await api.resolutionDispatchLog(id as string, cursor ? { cursor } : undefined);
      if (!cursor) return response;
      return {
        ...response,
        data: mergeDispatchLogRows(previous?.data ?? [], response.data ?? [])
      };
    },
    enabled: !!id && tab === "versand",
    staleTime: LOG_STALE_TIME_MS,
    placeholderData: (previousData) => previousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });
  const triggerNotificationLogQuery = useQuery({
    queryKey: ["resolution-trigger-notifications-log", id],
    queryFn: () => api.resolutionTriggerNotificationsLog(id as string),
    enabled: !!id && tab === "versand",
    staleTime: LOG_STALE_TIME_MS,
    placeholderData: (previousData) => previousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });
  const generatedDispatchQuery = useQuery({
    queryKey: ["generated-dispatch", id],
    queryFn: () => api.generatedDispatchDocuments(id as string),
    enabled: !!id && needsRecipientDataset,
    staleTime: LOG_STALE_TIME_MS,
    placeholderData: (previousData) => previousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const recipientQueryParams = useMemo(() => {
    if (tab === "empfaenger") {
      return {
        page: empfaengerPage,
        pageSize: RECIPIENT_PAGE_SIZE,
        search: searchEmpfaenger.trim() || undefined
      };
    }
    if (tab === "stimmen") {
      return {
        page: stimmenPage,
        pageSize: RECIPIENT_PAGE_SIZE,
        search: searchStimmen.trim() || undefined,
        erreichbarkeit: filterStimmenErreichbarkeit === "alle" ? undefined : filterStimmenErreichbarkeit,
        voteValue: filterStimmenverhalten === "alle" ? undefined : filterStimmenverhalten
      };
    }
    return {
      page: 1,
      pageSize: RECIPIENT_BULK_PAGE_SIZE
    };
  }, [empfaengerPage, filterStimmenErreichbarkeit, filterStimmenverhalten, searchEmpfaenger, searchStimmen, stimmenPage, tab]);

  const recipientsQuery = useQuery({
    queryKey: [
      "resolution-recipients",
      id,
      tab,
      recipientQueryParams.page,
      recipientQueryParams.pageSize,
      recipientQueryParams.search ?? "",
      recipientQueryParams.erreichbarkeit ?? "",
      recipientQueryParams.voteValue ?? ""
    ],
    queryFn: () => api.resolutionRecipients(id as string, recipientQueryParams),
    enabled: !!id && needsRecipientDataset,
    staleTime: READ_STALE_TIME_MS,
    placeholderData: (previousData) => previousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const documentsQuery = useQuery({
    queryKey: ["resolution-documents", id],
    queryFn: () => api.resolutionDocuments(id as string),
    enabled: !!id && (tab === "dokumente" || tab === "facilioo_dokumente"),
    staleTime: READ_STALE_TIME_MS,
    placeholderData: (previousData) => previousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });
  const faciliooAttachmentsQuery = useQuery({
    queryKey: ["resolution-facilioo-attachments", id, resolution?.facilioo_vorgangsnummer],
    queryFn: () => api.resolutionFaciliooAttachments(id as string),
    enabled: !!id && !!resolution?.facilioo_vorgangsnummer && tab === "facilioo_dokumente",
    staleTime: READ_STALE_TIME_MS,
    placeholderData: (previousData) => previousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });
  const faciliooFileTypesDebugQuery = useQuery({
    queryKey: ["resolution-facilioo-file-types-debug", id],
    queryFn: () => api.resolutionFaciliooFileTypesDebug(id as string),
    enabled: !!id && showFileTypesDebug
  });
  const ownerAccessesQuery = useQuery({
    queryKey: ["resolution-owner-accesses", id],
    queryFn: () => api.resolutionOwnerAccesses(id as string),
    enabled: !!id && tab === "empfaenger",
    staleTime: READ_STALE_TIME_MS,
    placeholderData: (previousData) => previousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const startMutation = useMutation({
    mutationFn: async (payload: { resolutionId: string; endedatum?: string }) => {
      const expectedUpdatedAt = expectedResolutionUpdatedAt;
      let effectiveUpdatedAt = expectedUpdatedAt;
      if (payload.endedatum) {
        const updated = await api.updateResolution(payload.resolutionId, {
          endedatum: payload.endedatum
        });
        effectiveUpdatedAt = updated.updatedAt;
      }
      return api.startResolution(payload.resolutionId, {
        expectedUpdatedAt: effectiveUpdatedAt,
        idempotency_key: createCommandIdempotencyKey("start")
      });
    },
    onSuccess: async (updatedResolution) => {
      setStartConfirm(null);
      setActionError("");
      const resolutionId = id as string;
      qc.setQueryData(["resolution", resolutionId], updatedResolution);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["resolution-result", resolutionId] }),
        qc.invalidateQueries({ queryKey: ["resolution-kpis", resolutionId] }),
        qc.invalidateQueries({ queryKey: ["resolution-dispatch-log", resolutionId] }),
        qc.invalidateQueries({ queryKey: ["resolution-trigger-notifications-log", resolutionId] }),
        qc.invalidateQueries({ queryKey: ["generated-dispatch", resolutionId] }),
        qc.invalidateQueries({ queryKey: ["resolution-owner-accesses", resolutionId] }),
        qc.invalidateQueries({ queryKey: ["resolutions"] })
      ]);

      // Versanddaten entstehen teils asynchron nach dem Start.
      // Kurzer Burst-Refresh sorgt für sofort sichtbare Änderungen im Versandprozess.
      if (tab === "versand") {
        const delayedRefetch = (delayMs: number) =>
          globalThis.setTimeout(() => {
            void Promise.all([
              qc.refetchQueries({ queryKey: ["resolution", resolutionId], type: "active" }),
              qc.refetchQueries({ queryKey: ["resolution-result", resolutionId], type: "active" }),
              qc.refetchQueries({ queryKey: ["resolution-kpis", resolutionId], type: "active" }),
              qc.refetchQueries({ queryKey: ["resolution-dispatch-log", resolutionId], type: "active" }),
              qc.refetchQueries({ queryKey: ["resolution-trigger-notifications-log", resolutionId], type: "active" }),
              qc.refetchQueries({ queryKey: ["generated-dispatch", resolutionId], type: "active" })
            ]);
          }, delayMs);

        delayedRefetch(2_000);
        delayedRefetch(6_000);
      }
    },
    onError: (e) => setActionError(e instanceof Error ? e.message : "Beschluss konnte nicht gestartet werden")
  });

  const stopMutation = useMutation({
    mutationFn: (resolutionId: string) =>
      api.stopResolution(resolutionId, {
        expectedUpdatedAt: expectedResolutionUpdatedAt,
        idempotency_key: createCommandIdempotencyKey("stop")
      }),
    onSuccess: async (updatedResolution) => {
      setStopConfirm(null);
      setActionError("");
      const resolutionId = id as string;
      qc.setQueryData(["resolution", resolutionId], updatedResolution);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["resolution-result", resolutionId] }),
        qc.invalidateQueries({ queryKey: ["resolution-kpis", resolutionId] }),
        qc.invalidateQueries({ queryKey: ["resolution-dispatch-log", resolutionId] }),
        qc.invalidateQueries({ queryKey: ["resolution-trigger-notifications-log", resolutionId] }),
        qc.invalidateQueries({ queryKey: ["generated-dispatch", resolutionId] }),
        qc.invalidateQueries({ queryKey: ["resolutions"] })
      ]);
    },
    onError: (e) => setActionError(e instanceof Error ? e.message : "Beschluss konnte nicht gestoppt werden")
  });

  const ownerResultVisibilityMutation = useMutation({
    mutationFn: (visible: boolean) =>
      api.updateResolution(id as string, {
        owner_einzelstimmabgabe_sichtbar: visible
      }),
    onSuccess: async (updatedResolution) => {
      setActionError("");
      qc.setQueryData(["resolution", id], updatedResolution);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["resolution-result", id] }),
        qc.invalidateQueries({ queryKey: ["resolution-kpis", id] })
      ]);
    },
    onError: (e) => setActionError(e instanceof Error ? e.message : "Einstellung konnte nicht gespeichert werden")
  });

  const documentUploadMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Ungültige Beschluss-ID");
      if (!documentFiles.length) throw new Error("Bitte mindestens eine Datei auswählen.");
      return api.uploadResolutionDocuments(id, documentFiles);
    },
    onSuccess: async () => {
      setDocumentError("");
      setDocumentInfo("");
      setDocumentFiles([]);
      await qc.invalidateQueries({ queryKey: ["resolution-documents", id] });
    },
    onError: (e) => setDocumentError(e instanceof Error ? e.message : "Dokumente konnten nicht hochgeladen werden")
  });

  const mergeDocumentFiles = (incoming: File[], replace = false) => {
    setDocumentFiles((prev) => {
      const base = replace ? [] : prev;
      const deduped = new Map<string, File>();
      for (const file of base) {
        deduped.set(`${file.name}__${file.size}__${file.lastModified}`, file);
      }
      for (const file of incoming) {
        deduped.set(`${file.name}__${file.size}__${file.lastModified}`, file);
      }
      return Array.from(deduped.values());
    });
  };

  const onDocumentDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDocumentDropActive(false);
    mergeDocumentFiles(Array.from(event.dataTransfer.files ?? []), false);
  };
  const documentDeleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      if (!id) throw new Error("Ungültige Beschluss-ID");
      await api.deleteResolutionDocument(id, documentId);
    },
    onSuccess: async () => {
      setDocumentError("");
      setDocumentInfo("");
      await qc.invalidateQueries({ queryKey: ["resolution-documents", id] });
    },
    onError: (e) => setDocumentError(e instanceof Error ? e.message : "Dokument konnte nicht gelöscht werden")
  });
  const uploadDocumentsToFaciliooMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Ungültige Beschluss-ID");
      const fallbackAllIds = documentRows.map((doc) => doc.id).filter(Boolean);
      const documentIds = selectedDocumentIds.length ? selectedDocumentIds : fallbackAllIds;
      if (!documentIds.length) throw new Error("Es sind keine Dokumente in der Liste vorhanden.");
      return api.uploadResolutionDocumentsToFacilioo(id, documentIds);
    },
    onSuccess: async (result) => {
      setDocumentError("");
      setDocumentInfo(`Zu Facilioo übertragen: ${result.uploadedCount}. Übersprungen: ${result.skippedCount}.`);
      setSelectedDocumentIds([]);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["resolution-documents", id] }),
        qc.invalidateQueries({ queryKey: ["resolution-facilioo-attachments", id] })
      ]);
    },
    onError: (e) => {
      setDocumentInfo("");
      setDocumentError(e instanceof Error ? e.message : "Dokumente konnten nicht zu Facilioo übertragen werden.");
    }
  });
  const importFaciliooAttachmentsMutation = useMutation({
    mutationFn: async (mode: "selected" | "all" = "selected") => {
      if (!id) throw new Error("Ungültige Beschluss-ID");
      const rows = faciliooAttachmentsQuery.data?.data ?? [];
      const selectedRows =
        mode === "all"
          ? rows
          : rows.filter((row) => selectedFaciliooAttachmentKeys.includes(`${row.feedId}:${row.attachmentId}`));
      if (!selectedRows.length) {
        throw new Error(mode === "all" ? "Keine Facilioo-Dokumente zum Neu-Laden verfügbar." : "Bitte mindestens ein Facilioo-Dokument auswählen.");
      }
      return api.importResolutionFaciliooAttachments(
        id,
        selectedRows.map((row) => ({
          feedId: row.feedId,
          attachmentId: row.attachmentId,
          originalFilename: resolveAttachmentFilename(row)
        }))
      );
    },
    onSuccess: async () => {
      setFaciliooDocumentError("");
      setSelectedFaciliooAttachmentKeys([]);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["resolution-documents", id] }),
        qc.invalidateQueries({ queryKey: ["resolution-facilioo-attachments", id] })
      ]);
    },
    onError: (e) => setFaciliooDocumentError(e instanceof Error ? e.message : "Facilioo-Dokumente konnten nicht importiert werden")
  });
  const previewFaciliooAttachmentMutation = useMutation({
    mutationFn: async (row: ResolutionFaciliooAttachmentRow) => {
      if (!id) throw new Error("Ungültige Beschluss-ID");
      const resolvedName = resolveAttachmentFilename(row);
      const blob = await api.fetchResolutionFaciliooAttachmentBlob(id, row.feedId, row.attachmentId, {
        fileName: resolvedName,
        fileExtension: row.fileExtension ?? undefined,
        mimeType: row.mimeType ?? undefined,
        download: false
      });
      return {
        name: resolvedName,
        url: URL.createObjectURL(blob),
        previewable: isPreviewableFaciliooAttachment(row)
      };
    },
    onSuccess: (payload) => {
      setFaciliooDocumentError("");
      setPreviewFaciliooDocument((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return payload;
      });
    },
    onError: (e) => setFaciliooDocumentError(e instanceof Error ? e.message : "Vorschau konnte nicht geladen werden")
  });
  const voteMutation = useMutation({
    mutationFn: (payload: { assignmentId: string; voteValue: VoteValue; comment?: string }) =>
      api.upsertResolutionVote(id as string, {
        ...payload,
        expectedUpdatedAt: expectedResolutionUpdatedAt
      }),
    onMutate: async (payload) => {
      if (!id) return {};
      await qc.cancelQueries({ queryKey: ["resolution-recipients", id] });
      const previousRecipientQueries = qc.getQueriesData<ListResult<ResolutionRecipientRow>>({
        queryKey: ["resolution-recipients", id]
      });
      const now = new Date().toISOString();
      for (const [queryKey, previousRecipients] of previousRecipientQueries) {
        if (!previousRecipients) continue;
        const ownerId = previousRecipients.data.find((row) => row.assignmentId === payload.assignmentId)?.eigentuemerId;
        if (ownerId) {
          const nextData = previousRecipients.data.map((row) =>
            row.eigentuemerId === ownerId
              ? {
                  ...row,
                  voteValue: payload.voteValue,
                  comment: payload.comment,
                  voteReceivedAt: now
                }
              : row
          );
          qc.setQueryData<ListResult<ResolutionRecipientRow>>(queryKey, {
            ...previousRecipients,
            data: nextData
          });
        }
      }
      return { previousRecipientQueries };
    },
    onSuccess: async () => {
      setActionError("");
      setVoteChangeConfirm(null);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["resolution-recipients", id] }),
        qc.invalidateQueries({ queryKey: ["resolution", id] }),
        qc.invalidateQueries({ queryKey: ["resolution-result", id] }),
        qc.invalidateQueries({ queryKey: ["resolution-kpis", id] }),
        qc.invalidateQueries({ queryKey: ["resolution-vote-log", id] }),
        qc.invalidateQueries({ queryKey: ["resolutions"] })
      ]);
    },
    onError: (e, _payload, context) => {
      if (id && context?.previousRecipientQueries) {
        for (const [queryKey, previousRecipients] of context.previousRecipientQueries) {
          qc.setQueryData(queryKey, previousRecipients);
        }
      }
      setActionError(e instanceof Error ? e.message : "Stimme konnte nicht gespeichert werden");
    }
  });

  const generatedDispatchRows: GeneratedDispatchRow[] = generatedDispatchQuery.data?.data ?? [];
  const temporaryAnalogOwnerIds = useMemo(() => {
    const currentEmailByOwnerId = new Map<string, string>();
    for (const row of recipientsQuery.data?.data ?? []) {
      const ownerId = String(row.eigentuemerId ?? "").trim();
      const email = String(row.email ?? "").trim();
      if (ownerId && isValidEmailLike(email)) currentEmailByOwnerId.set(ownerId, email);
    }
    const set = new Set<string>();
    for (const row of generatedDispatchRows) {
      const ownerId = String(row.ownerId ?? "").trim();
      const eigentuemerId = String(row.eigentuemerId ?? "").trim();
      if (!ownerId && !eigentuemerId) continue;
      if (row.kanal !== "digital") continue;
      if (row.templateKey === "ballot_analog") continue;
      const currentEmail = currentEmailByOwnerId.get(eigentuemerId);
      const currentEmailNormalized = normalizeEmail(currentEmail);
      const rowMailTo = String(row.digitalMailTo ?? "").trim();
      const rowMailToNormalized = normalizeEmail(rowMailTo);
      const failureAppliesToStaleEmail =
        isValidEmailLike(currentEmail) &&
        isValidEmailLike(rowMailTo) &&
        normalizeEmail(currentEmail) !== rowMailToNormalized;
      if (failureAppliesToStaleEmail) continue;
      const hasSuccessfulCurrentEmailDeliveryProof =
        Boolean(currentEmailNormalized) &&
        generatedDispatchRows.some(
          (candidate) =>
            isGeneratedDispatchRowForOwner(candidate, eigentuemerId) &&
            candidate.kanal === "digital" &&
            candidate.templateKey !== "ballot_analog" &&
            candidate.digitalMailStatus === "sent" &&
            normalizeEmail(candidate.digitalMailTo) === currentEmailNormalized
        );
      const permanentByCode =
        /^5\./.test(String(row.digitalMailNdrEnhancedStatusCode ?? "").trim()) ||
        /^5\d{2}$/.test(String(row.digitalMailNdrSmtpStatusCode ?? "").trim());
      const exhaustedTemporary =
        row.digitalMailStatus === "failed" &&
        !permanentByCode &&
        row.digitalMailNdrFailureClass !== "permanent" &&
        (row.digitalMailRetries ?? 0) >= DIGITAL_MAIL_MAX_ATTEMPTS;
      const rowActivityAt = getGeneratedDispatchRowActivityAt(row);
      const hasLaterSuccessfulDeliveryProof =
        Boolean(rowMailToNormalized) &&
        generatedDispatchRows.some((candidate) => {
          const sameOwner = isGeneratedDispatchRowForOwner(candidate, eigentuemerId) || isGeneratedDispatchRowForOwner(candidate, ownerId);
          if (!sameOwner) return false;
          if (candidate.id === row.id) return false;
          if (candidate.kanal !== "digital") return false;
          if (candidate.templateKey === "ballot_analog") return false;
          if (candidate.digitalMailStatus !== "sent") return false;
          if (normalizeEmail(candidate.digitalMailTo) !== rowMailToNormalized) return false;
          const candidateActivityAt = getGeneratedDispatchRowActivityAt(candidate);
          return !rowActivityAt || !candidateActivityAt || candidateActivityAt.localeCompare(rowActivityAt) >= 0;
        });
      if (
        !hasSuccessfulCurrentEmailDeliveryProof &&
        !hasLaterSuccessfulDeliveryProof &&
        (row.temporaryAnalogRequiredAt ||
          row.temporaryAnalogConvertedAt ||
          row.digitalMailNdrFailureClass === "permanent" ||
          permanentByCode ||
          exhaustedTemporary)
      ) {
        if (ownerId) set.add(ownerId);
        if (eigentuemerId) set.add(eigentuemerId);
      }
    }
    return set;
  }, [generatedDispatchRows, recipientsQuery.data?.data]);

  const restoredDigitalOwnerIds = useMemo(() => {
    const set = new Set<string>();
    for (const recipient of recipientsQuery.data?.data ?? []) {
      const eigentuemerId = String(recipient.eigentuemerId ?? "").trim();
      const email = String(recipient.email ?? "").trim();
      const normalizedEmail = normalizeEmail(email);
      if (!eigentuemerId || !isValidEmailLike(email)) continue;
      const hasSuccessfulCurrentEmailDeliveryProof = generatedDispatchRows.some(
        (candidate) =>
          isGeneratedDispatchRowForOwner(candidate, eigentuemerId) &&
          candidate.kanal === "digital" &&
          candidate.templateKey !== "ballot_analog" &&
          candidate.digitalMailStatus === "sent" &&
          normalizeEmail(candidate.digitalMailTo) === normalizedEmail
      );
      if (hasSuccessfulCurrentEmailDeliveryProof) set.add(eigentuemerId);
    }
    return set;
  }, [generatedDispatchRows, recipientsQuery.data?.data]);

  const recipientRows = useMemo<RecipientRow[]>(() => {
    const rows = recipientsQuery.data?.data ?? [];
    if (!rows.length) return [];
    return rows.map<RecipientRow>((row) => {
      const isForcedAnalog = temporaryAnalogOwnerIds.has(row.eigentuemerId);
      const isRestoredDigital = restoredDigitalOwnerIds.has(row.eigentuemerId);
      return {
        ...row,
        erreichbarkeit: isForcedAnalog ? "analog" : isRestoredDigital ? "digital" : row.erreichbarkeit,
        temporaryAnalog: isForcedAnalog ? true : undefined
      };
    });
  }, [recipientsQuery.data?.data, restoredDigitalOwnerIds, temporaryAnalogOwnerIds]);

  const filteredRecipientRowsEmpfaenger = useMemo(() => {
    if (tab === "empfaenger") return recipientRows;
    const needle = searchEmpfaenger.trim().toLowerCase();
    if (!needle) return recipientRows;
    return recipientRows.filter((r) =>
      [r.einheitsnummer, r.lage, r.eigentuemerId, r.eigentuemerName, r.email ?? "", r.erreichbarkeit].join(" ").toLowerCase().includes(needle)
    );
  }, [recipientRows, searchEmpfaenger, tab]);
  const [recipientTableScrollTop, setRecipientTableScrollTop] = useState(0);
  const recipientViewportHeight = 500;
  const recipientVisibleRange = useMemo(() => {
    const total = filteredRecipientRowsEmpfaenger.length;
    if (!total) return { start: 0, end: 0 };
    const rawStart = Math.floor(recipientTableScrollTop / RECIPIENT_TABLE_ROW_HEIGHT);
    const start = Math.max(rawStart - RECIPIENT_TABLE_OVERSCAN, 0);
    const visibleCount = Math.ceil(recipientViewportHeight / RECIPIENT_TABLE_ROW_HEIGHT) + RECIPIENT_TABLE_OVERSCAN * 2;
    const end = Math.min(start + visibleCount, total);
    return { start, end };
  }, [filteredRecipientRowsEmpfaenger.length, recipientTableScrollTop]);
  const visibleRecipientRowsEmpfaenger = useMemo(
    () => filteredRecipientRowsEmpfaenger.slice(recipientVisibleRange.start, recipientVisibleRange.end),
    [filteredRecipientRowsEmpfaenger, recipientVisibleRange.end, recipientVisibleRange.start]
  );
  const recipientTopSpacerHeight = recipientVisibleRange.start * RECIPIENT_TABLE_ROW_HEIGHT;
  const recipientBottomSpacerHeight = Math.max(
    (filteredRecipientRowsEmpfaenger.length - recipientVisibleRange.end) * RECIPIENT_TABLE_ROW_HEIGHT,
    0
  );

  const filteredRecipientRowsStimmen = useMemo(() => {
    if (tab === "stimmen") return recipientRows;
    const needle = searchStimmen.trim().toLowerCase();
    return recipientRows.filter((r) => {
      const matchesText = !needle || [r.einheitsnummer, r.lage, r.eigentuemerId, r.eigentuemerName, r.email ?? "", r.erreichbarkeit].join(" ").toLowerCase().includes(needle);
      const matchesErreichbarkeit = filterStimmenErreichbarkeit === "alle" || r.erreichbarkeit === filterStimmenErreichbarkeit;
      const matchesStimmverhalten = filterStimmenverhalten === "alle" || r.voteValue === filterStimmenverhalten;
      return matchesText && matchesErreichbarkeit && matchesStimmverhalten;
    });
  }, [filterStimmenErreichbarkeit, filterStimmenverhalten, recipientRows, searchStimmen, tab]);
  const [voteTableScrollTop, setVoteTableScrollTop] = useState(0);
  const voteViewportHeight = 520;
  const voteVisibleRange = useMemo(() => {
    const total = filteredRecipientRowsStimmen.length;
    if (!total) return { start: 0, end: 0 };
    const rawStart = Math.floor(voteTableScrollTop / VOTE_TABLE_ROW_HEIGHT);
    const start = Math.max(rawStart - VOTE_TABLE_OVERSCAN, 0);
    const visibleCount = Math.ceil(voteViewportHeight / VOTE_TABLE_ROW_HEIGHT) + VOTE_TABLE_OVERSCAN * 2;
    const end = Math.min(start + visibleCount, total);
    return { start, end };
  }, [filteredRecipientRowsStimmen.length, voteTableScrollTop]);
  const visibleRecipientRowsStimmen = useMemo(
    () => filteredRecipientRowsStimmen.slice(voteVisibleRange.start, voteVisibleRange.end),
    [filteredRecipientRowsStimmen, voteVisibleRange.end, voteVisibleRange.start]
  );
  const voteTopSpacerHeight = voteVisibleRange.start * VOTE_TABLE_ROW_HEIGHT;
  const voteBottomSpacerHeight = Math.max(
    (filteredRecipientRowsStimmen.length - voteVisibleRange.end) * VOTE_TABLE_ROW_HEIGHT,
    0
  );
  const recipientMetaTotal = recipientsQuery.data?.meta.total ?? recipientRows.length;
  const empfaengerTotalPages = Math.max(Math.ceil(recipientMetaTotal / RECIPIENT_PAGE_SIZE), 1);
  const stimmenTotalPages = Math.max(Math.ceil(recipientMetaTotal / RECIPIENT_PAGE_SIZE), 1);

  const filteredRecipientRowsErgebnis = useMemo(() => {
    const needle = searchErgebnis.trim().toLowerCase();
    if (!needle) return recipientRows;
    return recipientRows.filter((r) =>
      [r.einheitsnummer, r.lage, r.eigentuemerId, r.eigentuemerName, r.email ?? "", r.voteValue, r.comment ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [recipientRows, searchErgebnis]);
  const visibleRecipientRowsErgebnis = useMemo(() => {
    if (resultVotesRowCount === "all") return filteredRecipientRowsErgebnis;
    return filteredRecipientRowsErgebnis.slice(0, resultVotesRowCount);
  }, [filteredRecipientRowsErgebnis, resultVotesRowCount]);


  const versandEmpfaengerRows = useMemo(() => {
    const byOwner = new Map<
      string,
      {
        eigentuemerId: string;
        eigentuemerName: string;
        erreichbarkeit: "digital" | "analog";
        temporaryAnalog?: boolean;
        einheiten: Set<string>;
        emails: Set<string>;
        hasSubmittedVote: boolean;
      }
    >();

    for (const r of recipientRows) {
      const existing = byOwner.get(r.eigentuemerId);
      if (!existing) {
        byOwner.set(r.eigentuemerId, {
          eigentuemerId: r.eigentuemerId,
          eigentuemerName: r.eigentuemerName,
          erreichbarkeit: r.erreichbarkeit,
          temporaryAnalog: Boolean(r.temporaryAnalog),
          einheiten: new Set([r.einheitsnummer]),
          emails: new Set(r.email?.trim() ? [r.email.trim()] : []),
          hasSubmittedVote: r.voteValue !== "offen"
        });
      } else {
        existing.einheiten.add(r.einheitsnummer);
        if (r.temporaryAnalog) existing.temporaryAnalog = true;
        if (r.email?.trim()) existing.emails.add(r.email.trim());
        if (r.voteValue !== "offen") existing.hasSubmittedVote = true;
      }
    }

    const rows = [...byOwner.values()].map((row) => ({
      eigentuemerId: row.eigentuemerId,
      eigentuemerName: row.eigentuemerName,
      erreichbarkeit: row.erreichbarkeit,
      temporaryAnalog: Boolean(row.temporaryAnalog),
      hasSubmittedVote: row.hasSubmittedVote,
      einheitenLabel: [...row.einheiten]
        .sort((a, b) => a.localeCompare(b, "de"))
        .map((value) => formatUnitNumberForResult(value))
        .join(" | "),
      emailLabel: [...row.emails].sort((a, b) => a.localeCompare(b, "de")).join(" | ")
    }));

    const needle = searchVersand.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      [row.eigentuemerId, row.eigentuemerName, row.erreichbarkeit, row.temporaryAnalog ? "temporär analog" : "", row.einheitenLabel, row.emailLabel]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [recipientRows, searchVersand]);
  const versandEmpfaengerByOwnerId = useMemo(
    () => new Map(versandEmpfaengerRows.map((row) => [row.eigentuemerId, row] as const)),
    [versandEmpfaengerRows]
  );

  const digitalCount = recipientRows.filter((r) => r.erreichbarkeit === "digital").length;
  const analogCount = recipientRows.filter((r) => r.erreichbarkeit === "analog").length;
  const recipientTotal = digitalCount + analogCount;
  const overviewRecipientDigital = Number.isFinite(Number(kpiQuery.data?.recipient?.digital))
    ? Number(kpiQuery.data?.recipient?.digital ?? 0)
    : Number.isFinite(Number(resolution?.anzahl_digital_empfaenger))
    ? Number(resolution?.anzahl_digital_empfaenger ?? 0)
    : digitalCount;
  const overviewRecipientAnalog = Number.isFinite(Number(kpiQuery.data?.recipient?.analog))
    ? Number(kpiQuery.data?.recipient?.analog ?? 0)
    : Number.isFinite(Number(resolution?.anzahl_analog_empfaenger))
    ? Number(resolution?.anzahl_analog_empfaenger ?? 0)
    : analogCount;
  const overviewRecipientTotal = Math.max(
    Number.isFinite(Number(kpiQuery.data?.recipient?.gesamt))
      ? Number(kpiQuery.data?.recipient?.gesamt ?? 0)
      : Number.isFinite(Number(resolution?.anzahl_empfaenger_gesamt))
      ? Number(resolution?.anzahl_empfaenger_gesamt ?? 0)
      : overviewRecipientDigital + overviewRecipientAnalog,
    0
  );
  const overviewAbgegebeneStimmen = Number.isFinite(Number(kpiQuery.data?.abgegebene_stimmen))
    ? Number(kpiQuery.data?.abgegebene_stimmen ?? 0)
    : Number.isFinite(Number(resultQuery.data?.stimmen?.abgegeben))
    ? Number(resultQuery.data?.stimmen?.abgegeben ?? 0)
    : resolution?.abgegebene_stimmen ?? 0;
  const overviewRuecklaufquote = Number.isFinite(Number(kpiQuery.data?.ruecklaufquote))
    ? Number(kpiQuery.data?.ruecklaufquote ?? 0)
    : overviewRecipientTotal > 0
    ? (overviewAbgegebeneStimmen / overviewRecipientTotal) * 100
    : 0;
  const overviewStimmberechtigteEinheiten = resultQuery.data?.stimmberechtigte_einheiten?.length ?? resolution?.stimmberechtigte_einheiten.length ?? 0;
  const voteLogRows: ResolutionVoteLogRow[] = voteLogQuery.data?.data ?? [];
  const ownerNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of recipientRows) {
      map.set(row.eigentuemerId, row.eigentuemerName);
    }
    return map;
  }, [recipientRows]);
  const resolveVoteLogOwnerName = (ownerId?: string | null) => {
    if (!ownerId) return "-";
    return normalizeMojibake(ownerNameById.get(ownerId) ?? ownerId);
  };
  const dispatchLogRows: ResolutionDispatchLogRow[] = dispatchLogQuery.data?.data ?? [];
  const triggerNotificationLogRows: ResolutionTriggerNotificationLogRow[] = triggerNotificationLogQuery.data?.data ?? [];
  const uiSettings = loadUiSettings();
  const isFrameworkV2 = uiSettings.management.uiFramework === "mockup_v2";
  const getPlannedDispatchDate = (eventType: (typeof dispatchEventOrder)[number]) => {
    if (!resolution?.endedatum) return undefined;
    const match = resolution.endedatum.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return undefined;

    const baseDate = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (Number.isNaN(baseDate.getTime())) return undefined;

    const planned = new Date(baseDate);
    if (eventType === "erinnerung_1_versendet") planned.setDate(planned.getDate() - uiSettings.reminders.firstReminderDays);
    if (eventType === "erinnerung_2_versendet") planned.setDate(planned.getDate() - uiSettings.reminders.secondReminderDays);
    if (eventType === "beschlussergebnis_versendet") return resolution.endedatum;
    if (eventType === "einladung_versendet") return undefined;

    const y = planned.getFullYear();
    const m = String(planned.getMonth() + 1).padStart(2, "0");
    const d = String(planned.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const dispatchRows = dispatchEventOrder.map((eventType) => {
    const event = dispatchLogRows.find((row) => row.eventType === eventType);
    const generatedEventRows = generatedDispatchRows.filter((row) => row.eventType === eventType);
    const generatedPdfCount = generatedEventRows.filter((row) => Boolean(row.fileName && row.relativePath)).length;
    const deliverableRows = generatedEventRows.filter((row) => row.templateKey !== "ballot_analog");
    const digitalRows = deliverableRows.filter((row) => row.kanal === "digital");
    const analogRows = deliverableRows.filter((row) => row.kanal === "analog");
    const hasGeneratedSent = digitalRows.some((row) => row.digitalMailStatus === "sent");
    const hasAnalogPrepared = analogRows.some((row) => Boolean(row.fileName && row.relativePath));
    const hasGeneratedFailed = digitalRows.some((row) => row.digitalMailStatus === "failed");
    const hasGeneratedSkipped = digitalRows.length > 0 && digitalRows.every((row) => row.digitalMailStatus === "skipped");
    const latestGeneratedAt = generatedEventRows
      .map((row) => row.digitalMailSentAt)
      .filter((v): v is string => Boolean(v))
      .sort((a, b) => b.localeCompare(a))[0];
    const status: DispatchUiStatus = hasGeneratedSent || (digitalRows.length === 0 && hasAnalogPrepared)
      ? "versendet"
      : hasGeneratedFailed
        ? "fehlgeschlagen"
        : hasGeneratedSkipped
          ? "uebersprungen"
          : event
            ? "vorgemerkt"
            : "ausstehend";
    return {
      eventType,
      label: formatDispatchType(eventType),
      status,
      timestamp: latestGeneratedAt,
      plannedDate: getPlannedDispatchDate(eventType),
      generatedPdfCount
    };
  });
  const renderDispatchBadge = (
    eventType: (typeof dispatchEventOrder)[number],
    erreichbarkeit: "digital" | "analog",
    eigentuemerId?: string
  ) => {
    if (erreichbarkeit === "analog") {
      return <span className="badge inaktiv">Nicht digital</span>;
    }
    const ownerSentRows = generatedDispatchRows.filter((row) =>
      row.eventType === eventType &&
      row.kanal === "digital" &&
      isGeneratedDispatchRowForOwner(row, eigentuemerId) &&
      row.digitalMailStatus === "sent"
    );
    const ownerSent = ownerSentRows.length > 0;
    const ownerDigitalRows = generatedDispatchRows.filter((row) =>
      row.eventType === eventType &&
      row.kanal === "digital" &&
      isGeneratedDispatchRowForOwner(row, eigentuemerId) &&
      row.templateKey !== "ballot_analog"
    );
    const latestOwnerSent = ownerSentRows
      .slice()
      .sort((a, b) =>
        (b.digitalMailSentAt || b.digitalMailAttemptedAt || b.createdAt).localeCompare(
          a.digitalMailSentAt || a.digitalMailAttemptedAt || a.createdAt
        )
      )[0];
    const isReminderEvent = eventType === "erinnerung_1_versendet" || eventType === "erinnerung_2_versendet";
    const hasSubmittedVote = versandEmpfaengerByOwnerId.get(String(eigentuemerId ?? ""))?.hasSubmittedVote === true;
    const ownerStatus: DispatchUiStatus = ownerSent
      ? "versendet"
      : ownerDigitalRows.some((row) => row.digitalMailStatus === "failed")
        ? "fehlgeschlagen"
        : ownerDigitalRows.length > 0 && ownerDigitalRows.every((row) => row.digitalMailStatus === "skipped")
          ? "uebersprungen"
          : isReminderEvent && hasSubmittedVote && dispatchLogRows.some((row) => row.eventType === eventType)
            ? "abgestimmt"
          : dispatchLogRows.some((row) => row.eventType === eventType)
            ? "vorgemerkt"
            : "ausstehend";
    const sentAt = latestOwnerSent?.digitalMailSentAt || latestOwnerSent?.digitalMailAttemptedAt;
    const targetEmail = latestOwnerSent?.digitalMailTo || versandEmpfaengerByOwnerId.get(String(eigentuemerId ?? ""))?.emailLabel || "-";
    const title = ownerSent
      ? `Versendet am: ${formatDateTimeDE(sentAt)}\nZiel-E-Mail: ${targetEmail}`
      : ownerDigitalRows[0]?.digitalMailError
        ? ownerDigitalRows[0].digitalMailError
      : ownerStatus === "abgestimmt"
        ? "Nicht versendet, da bereits eine Stimme abgegeben wurde."
      : undefined;
    return (
      <span className={`badge ${getDispatchUiStatusBadgeClass(ownerStatus)}`} title={title}>
        {formatDispatchUiStatus(ownerStatus)}
      </span>
    );
  };

  const ownerAccessRows: OwnerVoteAccessRow[] = ownerAccessesQuery.data?.data ?? [];
  const documentRows: ResolutionDocumentRow[] = documentsQuery.data?.data ?? [];
  const faciliooAttachmentRows: ResolutionFaciliooAttachmentRow[] = faciliooAttachmentsQuery.data?.data ?? [];
  const faciliooFileTypeRows: ResolutionFaciliooFileTypeDebugRow[] = faciliooFileTypesDebugQuery.data?.data ?? [];
  useEffect(() => {
    const allowed = new Set(faciliooAttachmentRows.map((row) => `${row.feedId}:${row.attachmentId}`));
    setSelectedFaciliooAttachmentKeys((prev) => prev.filter((key) => allowed.has(key)));
  }, [faciliooAttachmentRows]);
  useEffect(() => {
    const allowed = new Set(documentRows.map((row) => row.id));
    setSelectedDocumentIds((prev) => prev.filter((id) => allowed.has(id)));
  }, [documentRows]);
  useEffect(() => {
    return () => {
      if (previewFaciliooDocument?.url) URL.revokeObjectURL(previewFaciliooDocument.url);
    };
  }, [previewFaciliooDocument?.url]);
  const ownerAccessByEigentuemerId = new Map<string, OwnerVoteAccessRow>();
  for (const row of ownerAccessRows) {
    if (!ownerAccessByEigentuemerId.has(row.eigentuemer_id)) ownerAccessByEigentuemerId.set(row.eigentuemer_id, row);
  }

  const filteredOwnerAccessRows = useMemo(() => {
    const needle = searchZugangsdaten.trim().toLowerCase();
    if (!needle) return ownerAccessRows;
    return ownerAccessRows.filter((row) =>
      [
        row.eigentuemer_id,
        row.eigentuemername,
        row.email ?? "",
        row.status,
        row.aktuelle_stimme,
        row.zugangspasswort ?? row.einmalpasswort
      ].join(" ").toLowerCase().includes(needle)
    );
  }, [ownerAccessRows, searchZugangsdaten]);

  const filteredDocumentRows = useMemo(() => {
    const needle = searchDokumente.trim().toLowerCase();
    if (!needle) return documentRows;
    return documentRows.filter((doc) =>
      [
        doc.originalName,
        doc.uploadedByName,
        formatDateTimeDE(doc.uploadedAt),
        `${(doc.size / 1024).toFixed(1)} KB`
      ].join(" ").toLowerCase().includes(needle)
    );
  }, [documentRows, searchDokumente]);

  const filteredFaciliooAttachmentRows = useMemo(() => {
    const needle = searchFaciliooDokumente.trim().toLowerCase();
    if (!needle) return faciliooAttachmentRows;
    return faciliooAttachmentRows.filter((row) => {
      const fileSizeBytes = Number.isFinite(Number(row.filesizeKb)) ? Number(row.filesizeKb) * 1024 : 0;
      return [
        row.originalFilename || `Anhang ${row.attachmentId}`,
        resolveFileTypeLabel(row),
        formatFileSize(fileSizeBytes),
        formatDateTimeDE(row.lastModifiedAt ?? row.createdAt ?? undefined),
        row.feedId,
        row.attachmentId
      ].join(" ").toLowerCase().includes(needle);
    });
  }, [faciliooAttachmentRows, searchFaciliooDokumente]);


  const triggerDownload = (content: string, fileName: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadDispatchZip = async (eventType: (typeof dispatchEventOrder)[number]) => {
    if (!id) return;
    try {
      setActionError("");
      const blob = await api.downloadGeneratedDispatchZip(id, eventType);
      const safeBeschlussnummer = String(resolution?.beschlussnummer ?? "beschluss").replace(/[^A-Za-z0-9_-]+/g, "_");
      const fileName = `Versand_${safeBeschlussnummer}_${eventType}.zip`;
      downloadBlob(blob, fileName);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "ZIP-Download fehlgeschlagen.");
    }
  };

  const buildLogFileName = (extension: "json" | "csv") => {
    const beschlussnummer = (resolution?.beschlussnummer ?? "unbekannt").replace(/[^A-Za-z0-9_-]/g, "_");
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    const stamp = `${yyyy}-${mm}-${dd}_${hh}-${min}`;
    return `Log_${beschlussnummer}_${stamp}.${extension}`;
  };

  const handleDownloadVoteLogJson = () => {
    const fileName = buildLogFileName("json");
    const enrichedRows = voteLogRows.map((row) => ({
      ...row,
      owner_display_name: resolveVoteLogOwnerName(row.owner_id)
    }));
    triggerDownload(JSON.stringify(enrichedRows, null, 2), fileName, "application/json;charset=utf-8");
  };

  const handleDownloadVoteLogCsv = () => {
    const fileName = buildLogFileName("csv");
    triggerDownload(buildVoteLogCsv(voteLogRows, resolveVoteLogOwnerName), fileName, "text/csv;charset=utf-8");
  };


  const buildOwnerAccessCsv = (rows: OwnerVoteAccessRow[]) => {
    const header = ["Eigentümer ID", "Eigentümer", "E-Mail", "Zugangspasswort", "Direktlink", "Status", "Aktuelle Stimme"];
    const body = rows.map((row) => [
      row.eigentuemer_id,
      row.eigentuemername,
      row.email ?? "",
      row.zugangspasswort ?? row.einmalpasswort,
      `${window.location.origin}${row.direct_path}`,
      row.status,
      row.aktuelle_stimme
    ]);

    const escapeCsv = (value: string) => `"${String(value).replace(/"/g, '""')}"`;
    return [header, ...body].map((row) => row.map((cell) => escapeCsv(String(cell))).join(";")).join("\n");
  };

  const handleDownloadOwnerAccessCsv = () => {
    const fileName = `Zugangsdaten_${(resolution?.beschlussnummer ?? "unbekannt").replace(/[^A-Za-z0-9_-]/g, "_")}.csv`;
    triggerDownload(buildOwnerAccessCsv(ownerAccessRows), fileName, "text/csv;charset=utf-8");
  };

  const openVoteLogModal = async () => {
    await voteLogQuery.refetch();
    setIsVoteLogModalOpen(true);
  };

  const isLoading =
    resolutionQuery.isLoading ||
    kpiQuery.isLoading ||
    resultQuery.isLoading ||
    recipientsQuery.isLoading ||
    ownerAccessesQuery.isLoading ||
    dispatchLogQuery.isLoading ||
    triggerNotificationLogQuery.isLoading ||
    generatedDispatchQuery.isLoading;
  const hasFaciliooProcessLink = Boolean(String(resolution?.facilioo_vorgangsnummer ?? "").trim());
  const visibleTabs = useMemo(
    () => tabs.filter((entry) => entry.key !== "facilioo_dokumente" || hasFaciliooProcessLink),
    [hasFaciliooProcessLink]
  );
  useEffect(() => {
    if (!hasFaciliooProcessLink && tab === "facilioo_dokumente") {
      setTab("stammdaten");
    }
  }, [hasFaciliooProcessLink, tab]);
  const liveSyncEnabled = RESOLUTION_SSE_ENABLED && (tab === "stammdaten" || tab === "stimmen" || tab === "versand" || tab === "ergebnis");
  useEffect(() => {
    if (!id || !liveSyncEnabled) return;
    const stream = api.subscribeResolutionEvents(id, {
      onEvent: (eventName) => {
        if (eventName !== "resolution-updated") return;
        void qc.invalidateQueries({ queryKey: ["resolution", id] });
        if (tab === "stammdaten" || tab === "stimmen" || tab === "ergebnis") {
          void qc.invalidateQueries({ queryKey: ["resolution-kpis", id] });
          void qc.invalidateQueries({ queryKey: ["resolution-result", id] });
        }
        if (tab === "stimmen") {
          void qc.invalidateQueries({ queryKey: ["resolution-vote-log", id] });
          void qc.invalidateQueries({ queryKey: ["resolution-recipients", id] });
        }
        if (tab === "versand") {
          void qc.invalidateQueries({ queryKey: ["resolution-dispatch-log", id] });
          void qc.invalidateQueries({ queryKey: ["resolution-trigger-notifications-log", id] });
          void qc.invalidateQueries({ queryKey: ["generated-dispatch", id] });
        }
      }
    });
    return () => stream.close();
  }, [id, liveSyncEnabled, qc, tab]);
  useEffect(() => {
    setRecipientTableScrollTop(0);
  }, [searchEmpfaenger, tab, filteredRecipientRowsEmpfaenger.length]);
  useEffect(() => {
    setVoteTableScrollTop(0);
  }, [searchStimmen, filterStimmenErreichbarkeit, filterStimmenverhalten, tab, filteredRecipientRowsStimmen.length]);
  useEffect(() => {
    setEmpfaengerPage(1);
  }, [searchEmpfaenger, tab]);
  useEffect(() => {
    setStimmenPage(1);
  }, [filterStimmenErreichbarkeit, filterStimmenverhalten, searchStimmen, tab]);
  useActionMenuAutoClose({
    isOpen: !!voteOpenActionMenuId,
    isHovered: isVoteActionMenuHovered,
    closeMenu: () => setVoteOpenActionMenuId(null)
  });
  useActionMenuAutoClose({
    isOpen: !!documentOpenActionMenuId,
    isHovered: isDocumentActionMenuHovered,
    closeMenu: () => setDocumentOpenActionMenuId(null)
  });
  useActionMenuAutoClose({
    isOpen: !!faciliooOpenActionMenuId,
    isHovered: isFaciliooActionMenuHovered,
    closeMenu: () => setFaciliooOpenActionMenuId(null)
  });
  useEffect(() => {
    if (!voteOpenActionMenuId) setIsVoteActionMenuHovered(false);
  }, [voteOpenActionMenuId]);
  useEffect(() => {
    if (!documentOpenActionMenuId) setIsDocumentActionMenuHovered(false);
  }, [documentOpenActionMenuId]);
  useEffect(() => {
    if (!faciliooOpenActionMenuId) setIsFaciliooActionMenuHovered(false);
  }, [faciliooOpenActionMenuId]);
  useEffect(() => {
    if (!voteOpenActionMenuId && !documentOpenActionMenuId && !faciliooOpenActionMenuId) {
      setActionMenuPosition(null);
    }
  }, [documentOpenActionMenuId, faciliooOpenActionMenuId, voteOpenActionMenuId]);
  useEffect(() => {
    const hasOpenMenu = !!voteOpenActionMenuId || !!documentOpenActionMenuId || !!faciliooOpenActionMenuId;
    if (!hasOpenMenu) return;

    const closeAllActionMenus = () => {
      setVoteOpenActionMenuId(null);
      setDocumentOpenActionMenuId(null);
      setFaciliooOpenActionMenuId(null);
    };
    const handlePointerDown = (event: MouseEvent) => {
      if (isActionMenuEventTarget(event.target)) return;
      closeAllActionMenus();
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeAllActionMenus();
    };

    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", closeAllActionMenus, true);
    window.addEventListener("resize", closeAllActionMenus);
    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", closeAllActionMenus, true);
      window.removeEventListener("resize", closeAllActionMenus);
    };
  }, [documentOpenActionMenuId, faciliooOpenActionMenuId, voteOpenActionMenuId]);
  useEffect(() => {
    setVoteOpenActionMenuId(null);
    setDocumentOpenActionMenuId(null);
    setFaciliooOpenActionMenuId(null);
  }, [filterStimmenErreichbarkeit, filterStimmenverhalten, searchStimmen, stimmenPage, tab]);

  const openFloatingActionMenu = (
    menuId: string,
    trigger: HTMLElement,
    setOpenMenuId: (value: string | null) => void,
    menuHeight = 112
  ) => {
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 170;
    const margin = 8;
    const left = Math.min(
      Math.max(margin, rect.right - menuWidth),
      Math.max(margin, window.innerWidth - menuWidth - margin)
    );
    const belowTop = rect.bottom + 6;
    const aboveTop = rect.top - menuHeight - 6;
    const top = belowTop + menuHeight <= window.innerHeight - margin ? belowTop : Math.max(margin, aboveTop);
    setActionMenuPosition({ left, top });
    setVoteOpenActionMenuId(null);
    setDocumentOpenActionMenuId(null);
    setFaciliooOpenActionMenuId(null);
    setOpenMenuId(menuId);
  };

  if (!id) {
    return <section className="panel">Ungültige Beschluss-ID.</section>;
  }

  if (resolutionQuery.isError) {
    return <section className="panel">Umlaufbeschluss konnte nicht geladen werden.</section>;
  }

  if (!resolution) {
    return isFrameworkV2
      ? <div className="resolution-detail-v2-page" />
      : <section className="panel">Lade Umlaufbeschluss...</section>;
  }

  const canCaptureVotes = resolution.status !== "entwurf";
  const autoDispatchTooltip = "Automatischer Versandprozess aktiv";
  const overviewKpiItems: Array<{
    label: string;
    value: ReactNode;
    note?: string;
    secondaryLabel?: string;
    secondaryValue?: ReactNode;
    secondaryNote?: string;
    icon?: ReactNode;
    variant?: "combined" | "remaining" | "facilioo";
  }> = [
    {
      label: "Stimmrechtsart",
      value: formatStimmrechtsart(resolution.stimmrechtsart),
      secondaryLabel: "Quorum",
      secondaryValue: formatQuorum(resolution.quorum),
      secondaryNote: quorumKpiHinweis,
      variant: "combined"
    },
    {
      label: "Restlaufzeit (Tage)",
      icon: <RemainingTimeIcon />,
      value: (
        <span className="resolutions-v2-remaining-bullet resolution-detail-remaining-bullet-v2">
          {getRemainingDaysForActiveResolution(resolution.status, resolution.endedatum)}
        </span>
      ),
      variant: "remaining"
    },
    { label: "Facilioo Vorgangsnummer", value: resolution.facilioo_vorgangsnummer ?? "-", variant: "facilioo" }
  ];
  const overviewResponsePct = Math.max(0, Math.min(100, overviewRuecklaufquote));
  const overviewOpenVotes = Math.max(overviewRecipientTotal - overviewAbgegebeneStimmen, 0);
  const overviewDigitalPct = overviewRecipientTotal > 0 ? (overviewRecipientDigital / overviewRecipientTotal) * 100 : 0;
  const overviewResponseRingStyle = { "--resolution-response-pct": `${overviewResponsePct}%` } as CSSProperties;
  const overviewRecipientRingStyle = { "--resolution-recipient-digital-pct": `${Math.max(0, Math.min(100, overviewDigitalPct))}%` } as CSSProperties;
  const overviewVoteSource = kpiQuery.data?.stimmen ?? resultQuery.data?.stimmen;
  const overviewVoteJa = Number.isFinite(Number(overviewVoteSource?.ja)) ? Number(overviewVoteSource?.ja ?? 0) : 0;
  const overviewVoteNein = Number.isFinite(Number(overviewVoteSource?.nein)) ? Number(overviewVoteSource?.nein ?? 0) : 0;
  const overviewVoteEnthaltung = Number.isFinite(Number(overviewVoteSource?.enthaltung)) ? Number(overviewVoteSource?.enthaltung ?? 0) : 0;
  const overviewVoteSourceTotal = Number.isFinite(Number(overviewVoteSource?.gesamt)) ? Number(overviewVoteSource?.gesamt ?? 0) : 0;
  const overviewVoteTotal = overviewVoteSourceTotal > 0
    ? overviewVoteSourceTotal
    : Math.max(overviewVoteJa + overviewVoteNein + overviewVoteEnthaltung, overviewRecipientTotal, 0);
  const overviewVoteOffen = Number.isFinite(Number(overviewVoteSource?.offen))
    ? Number(overviewVoteSource?.offen ?? 0)
    : Math.max(overviewVoteTotal - overviewVoteJa - overviewVoteNein - overviewVoteEnthaltung, 0);
  const getVoteSegmentWidth = (value: number) => (overviewVoteTotal > 0 ? `${Math.max((value / overviewVoteTotal) * 100, value > 0 ? 4 : 0)}%` : "0%");

  return (
    <>
      <div className={isFrameworkV2 ? "resolution-detail-v2-page" : undefined}>
      <section className={`panel page-header${isFrameworkV2 ? " resolution-detail-v2-header" : ""}`}>
        <div>
          <h2 className={isFrameworkV2 ? "resolution-detail-v2-title" : ""} style={isFrameworkV2 ? undefined : { margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
            {isFrameworkV2 ? (
              <span className="resolutions-v2-title-icon" aria-hidden="true">
                <ResolutionKpiIcon />
              </span>
            ) : null}
            <span className={isFrameworkV2 ? "resolution-detail-v2-title-copy" : undefined}>
              <span className={isFrameworkV2 ? "resolution-detail-v2-title-main" : undefined}>{resolution.beschlussnummer}</span>
              <span className={isFrameworkV2 ? "resolution-detail-v2-title-sub" : undefined}>{resolution.titel}</span>
            </span>
          </h2>
          <div className={isFrameworkV2 ? "resolution-detail-v2-meta" : undefined} style={isFrameworkV2 ? undefined : { marginTop: 6, color: "var(--text-soft)", display: "flex", gap: 10, flexWrap: "wrap" }}>
            <span>Objekt: <Link to={`/objects/${resolution.objectId}`}>{resolution.objektnummer} / {resolution.objektname}</Link></span>
            <span>Startdatum: {formatDateDE(resolution.startdatum)}</span>
            <span>Enddatum: {formatDateDE(resolution.endedatum)}</span>
          </div>
        </div>
        <div className={isFrameworkV2 ? "resolution-detail-v2-actions" : undefined} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span className={`badge ${resolution.status}`}>{formatStatus(resolution.status)}</span>
          {resolution.dispatch_automation_enabled && resolution.status === "aktiv" ? (
            <span
              className="dispatch-automation-indicator quick-tooltip"
              data-tooltip={autoDispatchTooltip}
              aria-label={autoDispatchTooltip}
              tabIndex={0}
            >
              ⚙
            </span>
          ) : null}
          {outcomeBadge(resolution.status, kpiQuery.data?.quorumErfuellt ?? resultQuery.data?.quorumErfuellt)}
          {resolution.status === "entwurf" ? (
            <button
              className="resolution-action-btn resolution-start-btn"
              disabled={startMutation.isPending}
              onClick={() => {
                if (!resolution.endedatum) {
                  setStartConfirm({ beschlussnummer: resolution.beschlussnummer, endedatum: "", error: "" });
                  return;
                }
                startMutation.mutate({ resolutionId: resolution.id, endedatum: resolution.endedatum });
              }}
            >
              Starten
            </button>
          ) : null}
          {resolution.status === "aktiv" ? (
            <button
              className="resolution-action-btn resolution-stop-btn"
              disabled={stopMutation.isPending}
              onClick={() =>
                setStopConfirm({
                  resolutionId: resolution.id,
                  beschlussnummer: resolution.beschlussnummer,
                  code: String(Math.floor(1000 + Math.random() * 9000)),
                  input: ["", "", "", ""],
                  error: ""
                })
              }
            >
              Stoppen
            </button>
          ) : null}
          <Link to="/resolutions"><button className={isFrameworkV2 ? "resolution-detail-v2-secondary-btn" : undefined}>Zur Liste</button></Link>
        </div>
      </section>

      <section className={isFrameworkV2 ? "object-tabbar object-tabbar-v2" : "panel object-tabbar"} style={isFrameworkV2 ? undefined : { paddingTop: 14, paddingBottom: 10 }}>
        {visibleTabs.map((t) => (
          <button key={t.key} className={`object-tabbtn${isFrameworkV2 ? " object-tabbtn-v2" : ""}${tab === t.key ? " active" : ""}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </section>


      <StartResolutionModal
        open={!!startConfirm}
        beschlussnummer={startConfirm?.beschlussnummer ?? ""}
        enddatum={startConfirm?.endedatum ?? ""}
        error={startConfirm?.error}
        isPending={startMutation.isPending}
        onClose={() => setStartConfirm(null)}
        onEnddatumChange={(value) => startConfirm && setStartConfirm({ ...startConfirm, endedatum: value, error: "" })}
        onConfirm={() => {
          if (!startConfirm) return;
          const today = getTodayDateKey();
          if (!startConfirm.endedatum) {
            setStartConfirm({ ...startConfirm, error: "Enddatum ist erforderlich." });
            return;
          }
          if (startConfirm.endedatum <= today) {
            setStartConfirm({ ...startConfirm, error: "Enddatum muss nach dem heutigen Startdatum liegen." });
            return;
          }
          startMutation.mutate({ resolutionId: resolution.id, endedatum: startConfirm.endedatum });
        }}
      />

      <DeleteCodeConfirmModal
        open={!!stopConfirm}
        title="Beschluss stoppen"
        message={stopConfirm ? `Beschluss ${stopConfirm.beschlussnummer} soll manuell gestoppt werden.` : ""}
        code={stopConfirm?.code ?? ""}
        input={stopConfirm?.input ?? ["", "", "", ""]}
        error={stopConfirm?.error}
        isPending={stopMutation.isPending}
        onClose={() => setStopConfirm(null)}
        onInputChange={(next) => stopConfirm && setStopConfirm({ ...stopConfirm, input: next })}
        onErrorReset={() => stopConfirm?.error && setStopConfirm({ ...stopConfirm, error: "" })}
        confirmLabel="Stoppen bestätigen"
        pendingLabel="Stoppe..."
        onConfirm={() => {
          if (!stopConfirm) return;
          if (stopConfirm.input.join("") !== stopConfirm.code) {
            setStopConfirm({ ...stopConfirm, error: "Sicherheitscode falsch eingegeben." });
            return;
          }
          stopMutation.mutate(stopConfirm.resolutionId);
        }}
      />

      <DeleteCodeConfirmModal
        open={!!voteChangeConfirm}
        title="Stimmabgabe ändern"
        message={voteChangeConfirm ? `Die Stimmabgabe von ${voteChangeConfirm.eigentuemerLabel} soll geändert werden.` : ""}
        code={voteChangeConfirm?.code ?? ""}
        input={voteChangeConfirm?.input ?? ["", "", "", ""]}
        error={voteChangeConfirm?.error}
        isPending={voteMutation.isPending}
        onClose={() => setVoteChangeConfirm(null)}
        onInputChange={(next) => voteChangeConfirm && setVoteChangeConfirm({ ...voteChangeConfirm, input: next })}
        onErrorReset={() => voteChangeConfirm?.error && setVoteChangeConfirm({ ...voteChangeConfirm, error: "" })}
        confirmLabel="Abstimmung ändern"
        pendingLabel="Ändere..."
        onConfirm={() => {
          if (!voteChangeConfirm) return;
          if (voteChangeConfirm.input.join("") !== voteChangeConfirm.code) {
            setVoteChangeConfirm({ ...voteChangeConfirm, error: "Sicherheitscode falsch eingegeben." });
            return;
          }
          voteMutation.mutate({
            assignmentId: voteChangeConfirm.assignmentId,
            voteValue: voteChangeConfirm.voteValue,
            comment: voteChangeConfirm.comment
          });
        }}
      />

      <DeleteCodeConfirmModal
        open={!!documentDeleteConfirm}
        title="Dokument löschen"
        message={documentDeleteConfirm ? `Dokument "${documentDeleteConfirm.documentName}" wirklich löschen?` : ""}
        code={documentDeleteConfirm?.code ?? ""}
        input={documentDeleteConfirm?.input ?? ["", "", "", ""]}
        error={documentDeleteConfirm?.error}
        isPending={documentDeleteMutation.isPending}
        onClose={() => setDocumentDeleteConfirm(null)}
        onInputChange={(next) => documentDeleteConfirm && setDocumentDeleteConfirm({ ...documentDeleteConfirm, input: next })}
        onErrorReset={() => documentDeleteConfirm?.error && setDocumentDeleteConfirm({ ...documentDeleteConfirm, error: "" })}
        confirmLabel="Löschen bestätigen"
        pendingLabel="Lösche..."
        onConfirm={() => {
          if (!documentDeleteConfirm) return;
          if (documentDeleteConfirm.input.join("") !== documentDeleteConfirm.code) {
            setDocumentDeleteConfirm({ ...documentDeleteConfirm, error: "Sicherheitscode falsch eingegeben." });
            return;
          }
          documentDeleteMutation.mutate(documentDeleteConfirm.documentId, {
            onSuccess: () => setDocumentDeleteConfirm(null)
          });
        }}
      />

      {isVoteLogModalOpen ? (
        <div className="modal-backdrop">
          <section className="panel modal-card" style={{ width: "min(1100px, 96vw)", maxWidth: 1100 }}>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>Logdatei</h3>
            <p style={{ marginTop: 0, color: "var(--text-soft)" }}>
              Protokollierte Stimmabgaben und Stimmänderungen für {resolution.beschlussnummer}.
            </p>
            <div className="form-actions" style={{ justifyContent: "flex-start", marginBottom: 8 }}>
              <button type="button" onClick={handleDownloadVoteLogJson}>Download JSON</button>
              <button type="button" onClick={handleDownloadVoteLogCsv}>Download CSV</button>
            </div>
            <div style={{ overflowX: "auto", maxHeight: "55vh", marginBottom: 10 }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>Zeitpunkt</th>
                    <th>Ereignis</th>
                    <th>IP</th>
                    <th>Kanal</th>
                    <th>Authentifizierung</th>
                    <th>Authentifiziert durch</th>
                    <th>Eigentümer</th>
                    <th>Stimme</th>
                    <th>Geändert</th>
                  </tr>
                </thead>
                <tbody>
                  {voteLogRows.map((log, idx) => {
                    const changed = Array.isArray(log.changed_assignments) ? log.changed_assignments : [];
                    const changedLabel = changed.length
                      ? changed.map((c) => `${c.previousVoteValue ?? "offen"} -> ${c.nextVoteValue ?? "offen"}`).join(", ")
                      : "-";

                    return (
                      <tr key={`vote-log-modal-${String(log.timestamp ?? "")}-${idx}`}>
                        <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>{formatDateTimeDE(log.timestamp)}</td>
                        <td>{formatVoteEventType(log.vote_event_type)}</td>
                        <td>{log.client_ip ?? "-"}</td>
                        <td>{formatSubmissionChannel(log.submission_channel)}</td>
                        <td>{formatAuthMode(log.auth_mode, log.auth_user_name ?? log.actor_name)}</td>
                        <td>{formatAuthUser(log.auth_user_name, log.auth_user_id)}</td>
                        <td>{resolveVoteLogOwnerName(log.owner_id)}</td>
                        <td>{log.vote_value ? formatVoteValue(log.vote_value) : "-"}</td>
                        <td>{changedLabel}</td>
                      </tr>
                    );
                  })}
                  {!voteLogRows.length ? <tr><td colSpan={9}>Keine Logeinträge vorhanden.</td></tr> : null}
                </tbody>
              </table>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setIsVoteLogModalOpen(false)}>Schließen</button>
            </div>
          </section>
        </div>
      ) : null}

      {isResultVotesModalOpen ? (
        <div className="modal-backdrop" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setIsResultVotesModalOpen(false);
        }}>
          <section className="panel modal-card result-votes-modal-card" role="dialog" aria-modal="true" aria-labelledby="result-votes-modal-title">
            <div className="result-votes-modal-head">
              <div>
                <h3 id="result-votes-modal-title" style={{ marginTop: 0, marginBottom: 6 }}>Einzelstimmen</h3>
                <p style={{ marginTop: 0, color: "var(--text-soft)" }}>
                  Einzelstimmabgaben für {resolution.beschlussnummer}.
                </p>
              </div>
              <button type="button" onClick={() => setIsResultVotesModalOpen(false)} aria-label="Overlay schließen">
                Schließen
              </button>
            </div>
            <section className={isFrameworkV2 ? "resolutions-v2-filterbar resolution-detail-v2-filterbar result-votes-modal-toolbar" : "filterbar result-votes-modal-toolbar"} style={isFrameworkV2 ? undefined : { marginBottom: 10 }}>
              <div className={isFrameworkV2 ? "resolutions-v2-filter-fields result-votes-modal-fields" : "result-votes-modal-fields"}>
                <label className={isFrameworkV2 ? "resolutions-v2-filter-input resolution-detail-v2-filter-input" : undefined}>
                  {isFrameworkV2 ? (
                    <span className="resolutions-v2-filter-icon" aria-hidden="true"><ResolutionDetailTableIcon name="search" /></span>
                  ) : null}
                  <input
                    className={isFrameworkV2 ? undefined : "table-field"}
                    placeholder="Suche..."
                    value={searchErgebnis}
                    onChange={(e) => setSearchErgebnis(e.target.value)}
                    style={isFrameworkV2 ? undefined : { minWidth: 320 }}
                  />
                </label>
                <label className="result-votes-row-count-field">
                  <span>Zeilen</span>
                  <select
                    className={isFrameworkV2 ? undefined : "table-field"}
                    value={String(resultVotesRowCount)}
                    onChange={(e) => {
                      const next = e.target.value;
                      setResultVotesRowCount(next === "all" ? "all" : Number(next));
                    }}
                  >
                    {RESULT_VOTE_ROW_COUNT_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                    <option value="all">Alle</option>
                  </select>
                </label>
              </div>
              <div className={isFrameworkV2 ? "resolutions-v2-row-count resolution-detail-v2-row-count result-votes-modal-count" : "result-votes-modal-count"}>
                {visibleRecipientRowsErgebnis.length} von {filteredRecipientRowsErgebnis.length} Treffern
              </div>
            </section>
            <div className="result-votes-modal-spacer" aria-hidden="true" />
            <div
              className={isFrameworkV2 ? "resolutions-v2-table-wrap resolution-detail-v2-table-wrap result-votes-modal-table-wrap" : "result-votes-modal-table-wrap"}
            >
              <table className={isFrameworkV2 ? "resolution-detail-v2-table resolution-detail-result-v2-table" : undefined}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Einheit" icon="unit" center /> : "Einheit"}</th>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Eigentümer" icon="ownerName" /> : "Eigentümer"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Stimmverhalten" icon="vote" center /> : "Stimmverhalten"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Letzte Änderung am" icon="date" center /> : "Letzte Änderung am"}</th>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Kommentar" icon="comment" /> : "Kommentar"}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRecipientRowsErgebnis.map((r) => (
                    <tr key={`result-modal-${r.assignmentId}`}>
                      <td style={{ textAlign: "center" }}>{formatUnitNumberForResult(r.einheitsnummer)}</td>
                      <td>{r.eigentuemerName}</td>
                      <td style={{ textAlign: "center" }}>
                        <span className={`badge ${r.voteValue === "ja" ? "vote-ja" : r.voteValue === "nein" ? "vote-nein" : r.voteValue === "enthaltung" ? "vote-enthaltung" : "vote-offen"}`}>
                          {formatVoteValue(r.voteValue)}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>{formatDateTimeDE(r.voteReceivedAt)}</td>
                      <td>{r.comment || "-"}</td>
                    </tr>
                  ))}
                  {!filteredRecipientRowsErgebnis.length ? <tr><td colSpan={5}>Keine stimmberechtigten Einheiten gefunden.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      ) : null}

      {actionError ? <section className="panel error-text">{actionError}</section> : null}
      {isLoading ? (
        isFrameworkV2
          ? null
          : <section className="panel">Daten werden geladen...</section>
      ) : null}

      {tab === "stammdaten" ? (
        isFrameworkV2 ? (
          <>
            <section className="object-overview-grid object-overview-grid-v2 resolution-detail-overview-grid-v2">
              {overviewKpiItems.map((item) => (
                <div
                  key={item.label}
                  className={`kpi object-overview-kpi-v2 resolution-detail-kpi-v2${item.variant ? ` resolution-detail-kpi-v2--${item.variant}` : ""}`}
                >
                  <span className="object-overview-kpi-icon" aria-hidden="true">{item.icon ?? <ResolutionKpiIcon />}</span>
                  <div className="object-overview-kpi-copy">
                    <div className="resolution-detail-kpi-part-v2">
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                      {item.note ? <small>{item.note}</small> : null}
                    </div>
                    {item.secondaryLabel ? (
                      <div className="resolution-detail-kpi-part-v2 resolution-detail-kpi-part-v2--secondary">
                        <span>{item.secondaryLabel}</span>
                        <strong>{item.secondaryValue}</strong>
                        {item.secondaryNote ? <small>{item.secondaryNote}</small> : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </section>

            <section className="object-overview-detail-grid-v2 resolution-detail-diagram-grid-v2">
              <article className="object-overview-detail-card-v2 resolution-detail-diagram-card-v2 resolution-detail-diagram-card-v2--return">
                <h3 className="object-overview-card-title-v2">
                  <span className="object-overview-card-title-icon-v2" aria-hidden="true"><ReturnRateIcon /></span>
                  <span>Rücklaufquote</span>
                </h3>
                <div className="object-reachability-v2 resolution-detail-donut-layout-v2">
                  <div className="object-reachability-ring-v2 resolution-detail-donut-v2 resolution-detail-donut-v2--response" style={overviewResponseRingStyle}>
                    <div className="object-reachability-ring-inner-v2">
                      <strong>{Math.round(overviewResponsePct)}%</strong>
                      <span>abgegeben</span>
                    </div>
                  </div>
                  <div className="object-reachability-legend-v2 resolution-detail-legend-v2 resolution-detail-return-legend-v2">
                    <div className="resolution-detail-return-legend-main-v2">
                      <div className="reachability-line resolution-detail-legend-line resolution-detail-legend-line--green">
                        <span className="dot dot-digital" /> <strong>Abgegeben</strong>
                        <small>{overviewAbgegebeneStimmen} Stimmen</small>
                        <b>{formatShare(overviewAbgegebeneStimmen, overviewRecipientTotal)} %</b>
                      </div>
                      <div className="reachability-line resolution-detail-legend-line resolution-detail-legend-line--red">
                        <span className="dot dot-analog" /> <strong>Offen</strong>
                        <small>{overviewOpenVotes} Stimmen</small>
                        <b>{formatShare(overviewOpenVotes, overviewRecipientTotal)} %</b>
                      </div>
                      <div className="reachability-total">
                        <span>Gesamt</span>
                        <strong>{overviewRecipientTotal} Empfänger</strong>
                      </div>
                    </div>
                    <div className="reachability-total resolution-detail-authorized-units-v2">
                      <span>Stimmberechtigte Einheiten</span>
                      <strong>{overviewStimmberechtigteEinheiten}</strong>
                    </div>
                  </div>
                </div>
              </article>

              <article className="object-overview-detail-card-v2 resolution-detail-diagram-card-v2">
                <h3 className="object-overview-card-title-v2">
                  <span className="object-overview-card-title-icon-v2" aria-hidden="true"><ReachabilityIcon /></span>
                  <span>Empfängerkanäle</span>
                </h3>
                <div className="object-reachability-v2 resolution-detail-donut-layout-v2">
                  <div className="object-reachability-ring-v2 resolution-detail-donut-v2 resolution-detail-donut-v2--recipients" style={overviewRecipientRingStyle}>
                    <div className="object-reachability-ring-inner-v2">
                      <strong>{Math.round(overviewDigitalPct)}%</strong>
                      <span>digital</span>
                    </div>
                  </div>
                  <div className="object-reachability-legend-v2 resolution-detail-legend-v2">
                    <div className="reachability-line resolution-detail-legend-line resolution-detail-legend-line--green">
                      <span className="dot dot-digital" /> <strong>Digital</strong>
                      <small>{overviewRecipientDigital} Empfänger</small>
                      <b>{formatShare(overviewRecipientDigital, overviewRecipientTotal)} %</b>
                    </div>
                    <div className="reachability-line resolution-detail-legend-line resolution-detail-legend-line--red">
                      <span className="dot dot-analog" /> <strong>Analog</strong>
                      <small>{overviewRecipientAnalog} Empfänger</small>
                      <b>{formatShare(overviewRecipientAnalog, overviewRecipientTotal)} %</b>
                    </div>
                    <div className="reachability-total">
                      <span>Gesamt</span>
                      <strong>{overviewRecipientTotal} Empfänger</strong>
                    </div>
                  </div>
                </div>
              </article>

              <article className="object-overview-detail-card-v2 resolution-detail-diagram-card-v2">
                <h3 className="object-overview-card-title-v2">
                  <span className="object-overview-card-title-icon-v2" aria-hidden="true"><ResultIcon /></span>
                  <span>Stimmenbild</span>
                </h3>
                <div className="resolution-detail-vote-chart-v2">
                  <div className="resolution-detail-vote-track-v2" aria-hidden="true">
                    <span className="resolution-detail-vote-segment-v2 resolution-detail-vote-segment-v2--yes" style={{ width: getVoteSegmentWidth(overviewVoteJa) }} />
                    <span className="resolution-detail-vote-segment-v2 resolution-detail-vote-segment-v2--no" style={{ width: getVoteSegmentWidth(overviewVoteNein) }} />
                    <span className="resolution-detail-vote-segment-v2 resolution-detail-vote-segment-v2--abstain" style={{ width: getVoteSegmentWidth(overviewVoteEnthaltung) }} />
                    <span className="resolution-detail-vote-segment-v2 resolution-detail-vote-segment-v2--open" style={{ width: getVoteSegmentWidth(overviewVoteOffen) }} />
                  </div>
                  <div className="object-reachability-legend-v2 resolution-detail-legend-v2 resolution-detail-vote-legend-v2">
                    <div className="reachability-line resolution-detail-legend-line resolution-detail-legend-line--green">
                      <span className="dot dot-digital" /> <strong>Ja</strong>
                      <small>{overviewVoteJa} Stimmen</small>
                      <b>{formatShare(overviewVoteJa, overviewVoteTotal)} %</b>
                    </div>
                    <div className="reachability-line resolution-detail-legend-line resolution-detail-legend-line--red">
                      <span className="dot dot-analog" /> <strong>Nein</strong>
                      <small>{overviewVoteNein} Stimmen</small>
                      <b>{formatShare(overviewVoteNein, overviewVoteTotal)} %</b>
                    </div>
                    <div className="reachability-line resolution-detail-legend-line resolution-detail-legend-line--yellow">
                      <span className="dot dot-digital" /> <strong>Enthaltung</strong>
                      <small>{overviewVoteEnthaltung} Stimmen</small>
                      <b>{formatShare(overviewVoteEnthaltung, overviewVoteTotal)} %</b>
                    </div>
                    <div className="reachability-line resolution-detail-legend-line resolution-detail-legend-line--neutral">
                      <span className="dot dot-analog" /> <strong>Offen</strong>
                      <small>{overviewVoteOffen} Stimmen</small>
                      <b>{formatShare(overviewVoteOffen, overviewVoteTotal)} %</b>
                    </div>
                  </div>
                  <div className="reachability-total resolution-detail-vote-total-v2">
                    <span>Gesamt</span>
                    <strong>{overviewVoteTotal} Stimmen</strong>
                  </div>
                </div>
              </article>
            </section>
          </>
        ) : (
          <section className="panel">
            <div className="object-overview-grid">
              {overviewKpiItems.map((item) => (
                <div key={item.label} className="kpi">
                  <span>{item.label}</span>
                  <strong style={{ fontSize: 18 }}>{item.value}</strong>
                  {item.note ? (
                    <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.35, color: "var(--text-soft)" }}>{item.note}</div>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        )
      ) : null}

      {tab === "beschlusstext" ? (
        <section className={isFrameworkV2 ? "panel resolution-detail-v2-content-card" : "panel"}>
          <div style={{ marginBottom: 18, fontWeight: 700, fontSize: "1.28em" }}>{resolution.titel}</div>
          <div style={{ marginBottom: 6, fontWeight: 700 }}>Beschlussantrag:</div>
          <div style={{ whiteSpace: "pre-wrap" }}>
            {resolution.beschlusstext}
          </div>
        </section>
       ) : null}
      {tab === "empfaenger" ? (
        <>
          <section className="panel resolution-detail-v2-list-panel">

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
              <h3 style={{ margin: 0 }}>Zugangsdaten je Eigentümer</h3>
              <button type="button" onClick={handleDownloadOwnerAccessCsv}>CSV exportieren</button>
            </div>
            <section className={isFrameworkV2 ? "resolutions-v2-filterbar resolution-detail-v2-filterbar" : "filterbar"} style={isFrameworkV2 ? undefined : { marginBottom: 10 }}>
              <div className={isFrameworkV2 ? "resolutions-v2-filter-fields" : undefined}>
                <label className={isFrameworkV2 ? "resolutions-v2-filter-input resolution-detail-v2-filter-input" : undefined}>
                  {isFrameworkV2 ? (
                    <span className="resolutions-v2-filter-icon" aria-hidden="true"><ResolutionDetailTableIcon name="search" /></span>
                  ) : null}
                  <input className={isFrameworkV2 ? undefined : "table-field"}
                    placeholder="Suche..."
                    value={searchZugangsdaten}
                    onChange={(e) => setSearchZugangsdaten(e.target.value)}
                    style={isFrameworkV2 ? undefined : { minWidth: 360 }}
                  />
                </label>
              </div>
            </section>
            {isFrameworkV2 ? (
              <div className="resolutions-v2-row-count resolution-detail-v2-row-count">Treffer: {filteredOwnerAccessRows.length} / {ownerAccessRows.length}</div>
            ) : null}

            <div className={isFrameworkV2 ? "resolutions-v2-table-wrap resolution-detail-v2-table-wrap" : undefined} style={isFrameworkV2 ? undefined : { overflowX: "auto" }}>
              <table className={isFrameworkV2 ? "resolution-detail-v2-table resolution-detail-access-v2-table" : undefined}>
                <thead>
                  <tr>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader icon="owner" label="Eigentümer ID" /> : "Eigentümer ID"}</th>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader icon="ownerName" label="Eigentümer" /> : "Eigentümer"}</th>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader icon="mail" label="E-Mail" /> : "E-Mail"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader icon="key" label="Zugangspasswort" center /> : "Zugangspasswort"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader icon="link" label="Direktlink" center /> : "Direktlink"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader icon="status" label="Passwort" center /> : "Passwort"}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOwnerAccessRows.map((row) => {
                    const directLink = `${window.location.origin}${row.direct_path}`;
                    return (
                      <tr key={row.id}>
                        <td>{row.eigentuemer_id}</td>
                        <td>{row.eigentuemername}</td>
                        <td>{row.email ?? "-"}</td>
                        <td style={{ textAlign: "center" }}><strong>{row.zugangspasswort ?? row.einmalpasswort}</strong></td>
                        <td style={{ textAlign: "center" }}>
                          <button
                            type="button"
                            onClick={async () => {
                              const copied = await copyTextToClipboard(directLink);
                              if (copied) {
                                setCopiedDirectLinkId(row.id);
                                globalThis.setTimeout(() => setCopiedDirectLinkId((prev) => (prev === row.id ? null : prev)), 1400);
                                return;
                              }
                              setActionError("Direktlink konnte nicht automatisch kopiert werden. Bitte manuell kopieren.");
                            }}
                            title={directLink}
                          >
                            {copiedDirectLinkId === row.id ? "Kopiert" : "Link kopieren"}
                          </button>
                        </td>
                        <td style={{ textAlign: "center" }}><span className={`badge ${row.status === "aktiv" ? "aktiv" : "inaktiv"}`}>{row.status === "aktiv" ? "unbenutzt" : row.status}</span></td>
                      </tr>
                    );
                  })}
                  {!filteredOwnerAccessRows.length ? <tr><td colSpan={6}>Keine Zugangsdaten vorhanden.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel resolution-detail-v2-list-panel">
            <h3 style={{ margin: "0 0 8px 0" }}>Abstimmungsberechtigte Einheiten</h3>
            <section className={isFrameworkV2 ? "resolutions-v2-filterbar resolution-detail-v2-filterbar" : "filterbar"} style={isFrameworkV2 ? undefined : { marginBottom: 10 }}>
              <div className={isFrameworkV2 ? "resolutions-v2-filter-fields" : undefined}>
                <label className={isFrameworkV2 ? "resolutions-v2-filter-input resolution-detail-v2-filter-input" : undefined}>
                  {isFrameworkV2 ? (
                    <span className="resolutions-v2-filter-icon" aria-hidden="true"><ResolutionDetailTableIcon name="search" /></span>
                  ) : null}
                  <input className={isFrameworkV2 ? undefined : "table-field"}
                    placeholder="Suche..."
                    value={searchEmpfaenger}
                    onChange={(e) => setSearchEmpfaenger(e.target.value)}
                    style={isFrameworkV2 ? undefined : { minWidth: 360 }}
                  />
                </label>
              </div>
              <div className={isFrameworkV2 ? "resolution-detail-v2-filter-actions" : undefined}>
                <span style={{ alignSelf: "center", color: "var(--text-soft)", fontSize: 12 }}>
                  Seite {empfaengerPage} / {empfaengerTotalPages}
                </span>
                <button type="button" onClick={() => setEmpfaengerPage((prev) => Math.max(prev - 1, 1))} disabled={empfaengerPage <= 1}>
                  Zurück
                </button>
                <button
                  type="button"
                  onClick={() => setEmpfaengerPage((prev) => Math.min(prev + 1, empfaengerTotalPages))}
                  disabled={empfaengerPage >= empfaengerTotalPages}
                >
                  Weiter
                </button>
              </div>
            </section>
            {isFrameworkV2 ? (
              <div className="resolutions-v2-row-count resolution-detail-v2-row-count">Treffer gesamt: {recipientMetaTotal}</div>
            ) : null}

            <div
              className={isFrameworkV2 ? "resolutions-v2-table-wrap resolution-detail-v2-table-wrap" : undefined}
              style={isFrameworkV2 ? { maxHeight: recipientViewportHeight } : { overflowX: "auto", maxHeight: recipientViewportHeight }}
              onScroll={(event) => setRecipientTableScrollTop(event.currentTarget.scrollTop)}
            >
              <table className={isFrameworkV2 ? "resolution-detail-v2-table resolution-detail-recipients-v2-table" : undefined}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader icon="unit" label="Einheit" center /> : "Einheit"}</th>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader icon="pin" label="Lage" /> : "Lage"}</th>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader icon="home" label="Typ" /> : "Typ"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader icon="owner" label="Eigentümer ID" center /> : "Eigentümer ID"}</th>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader icon="ownerName" label="Eigentümer" /> : "Eigentümer"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader icon="reachability" label="Erreichbarkeit" center /> : "Erreichbarkeit"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader icon="key" label="Stimmrecht" center /> : "Stimmrecht"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader icon="text" label="MEA" center /> : "MEA"}</th>
                  </tr>
                </thead>
                <tbody>
                  {recipientTopSpacerHeight > 0 ? (
                    <tr aria-hidden="true">
                      <td colSpan={8} style={{ height: recipientTopSpacerHeight, padding: 0, border: 0 }} />
                    </tr>
                  ) : null}
                  {visibleRecipientRowsEmpfaenger.map((r) => {
                    const reachabilityBadge = getReachabilityBadge(r.erreichbarkeit, r.temporaryAnalog);
                    return (
                      <tr key={r.assignmentId}>
                        <td style={{ textAlign: "center" }}>{formatUnitNumberForResult(r.einheitsnummer)}</td>
                        <td>{r.lage}</td>
                        <td>{r.einheitstyp}</td>
                        <td style={{ textAlign: "center" }}>{r.eigentuemerId}</td>
                        <td>{r.eigentuemerName}</td>
                        <td style={{ textAlign: "center" }}><span className={`badge ${reachabilityBadge.className}`}>{reachabilityBadge.label}</span></td>
                        <td style={{ textAlign: "center" }}>{r.stimmrecht}</td>
                        <td style={{ textAlign: "center" }}>{r.mea ?? "-"}</td>
                      </tr>
                    );
                  })}
                  {recipientBottomSpacerHeight > 0 ? (
                    <tr aria-hidden="true">
                      <td colSpan={8} style={{ height: recipientBottomSpacerHeight, padding: 0, border: 0 }} />
                    </tr>
                  ) : null}
                  {!filteredRecipientRowsEmpfaenger.length ? <tr><td colSpan={8}>Keine Empfänger gefunden.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}

      {tab === "stimmen" ? (
        <>
          <section className="panel">
            <div className="kpi-grid" style={{ marginBottom: 10 }}>
              <div className="kpi"><span>Digital erreichbar</span><strong>{digitalCount} ({formatShare(digitalCount, recipientTotal)} %)</strong></div>
              <div className="kpi"><span>Analog erreichbar</span><strong>{analogCount} ({formatShare(analogCount, recipientTotal)} %)</strong></div>
              <div className="kpi"><span>Stimmen eingegangen</span><strong>{filteredRecipientRowsStimmen.filter((r) => r.voteValue !== "offen").length}</strong></div>
              <div className="kpi"><span>Ausstehende Abstimmungen</span><strong>{filteredRecipientRowsStimmen.filter((r) => r.voteValue === "offen").length}</strong></div>
            </div>
          </section>

          <section className={isFrameworkV2 ? "panel resolution-detail-v2-list-panel" : "panel"}>
            <section className={isFrameworkV2 ? "resolutions-v2-filterbar resolution-detail-v2-filterbar" : "filterbar"} style={isFrameworkV2 ? undefined : { marginBottom: 10 }}>
              <div className={isFrameworkV2 ? "resolutions-v2-filter-fields" : undefined}>
                <label className={isFrameworkV2 ? "resolutions-v2-filter-input resolution-detail-v2-filter-input" : undefined}>
                  {isFrameworkV2 ? (
                    <span className="resolutions-v2-filter-icon" aria-hidden="true"><ResolutionDetailTableIcon name="search" /></span>
                  ) : null}
                  <input className={isFrameworkV2 ? undefined : "table-field"}
                    placeholder="Suche..."
                    value={searchStimmen}
                    onChange={(e) => setSearchStimmen(e.target.value)}
                    style={isFrameworkV2 ? undefined : { minWidth: 320 }}
                  />
                </label>
                <select className={isFrameworkV2 ? undefined : "table-field"} value={filterStimmenErreichbarkeit} onChange={(e) => setFilterStimmenErreichbarkeit(e.target.value as "alle" | "digital" | "analog") }>
                <option value="alle">Erreichbarkeit: Alle</option>
                <option value="digital">Erreichbarkeit: digital</option>
                <option value="analog">Erreichbarkeit: analog</option>
              </select>
                <select className={isFrameworkV2 ? undefined : "table-field"} value={filterStimmenverhalten} onChange={(e) => setFilterStimmenverhalten(e.target.value as "alle" | VoteValue)}>
                <option value="alle">Stimmverhalten: Alle</option>
                <option value="offen">Stimmverhalten: Offen</option>
                <option value="ja">Stimmverhalten: Ja</option>
                <option value="nein">Stimmverhalten: Nein</option>
                <option value="enthaltung">Stimmverhalten: Enthaltung</option>
              </select>
              </div>
              <div className={isFrameworkV2 ? "resolution-detail-v2-filter-actions" : undefined}>
                <span style={{ alignSelf: "center", color: "var(--text-soft)", fontSize: 12 }}>
                  Seite {stimmenPage} / {stimmenTotalPages}
                </span>
                <button type="button" onClick={() => setStimmenPage((prev) => Math.max(prev - 1, 1))} disabled={stimmenPage <= 1}>
                  Zurück
                </button>
                <button
                  type="button"
                  onClick={() => setStimmenPage((prev) => Math.min(prev + 1, stimmenTotalPages))}
                  disabled={stimmenPage >= stimmenTotalPages}
                >
                  Weiter
                </button>
              </div>
            </section>
            {isFrameworkV2 ? (
              <div className="resolutions-v2-row-count resolution-detail-v2-row-count">Treffer gesamt: {recipientMetaTotal}</div>
            ) : null}
            <div style={{ marginBottom: 8, color: "var(--text-soft)", fontSize: 12 }}>Eine Stimmabgabe gilt immer einheitlich für alle stimmberechtigten Einheiten desselben Eigentümers.</div>
            {!canCaptureVotes ? <div className="error-text" style={{ marginBottom: 8 }}>Stimmen können erst ab Start des Beschlusses erfasst werden.</div> : null}
            <div
              className={isFrameworkV2 ? "resolutions-v2-table-wrap resolution-detail-v2-table-wrap" : undefined}
              style={isFrameworkV2 ? { maxHeight: voteViewportHeight } : { overflowX: "auto", maxHeight: voteViewportHeight }}
              onScroll={(event) => setVoteTableScrollTop(event.currentTarget.scrollTop)}
            >
              <table className={isFrameworkV2 ? "resolution-detail-v2-table resolution-detail-votes-v2-table" : undefined}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Einheit" icon="unit" center /> : "Einheit"}</th>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Eigentümer" icon="ownerName" /> : "Eigentümer"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Erreichbarkeit" icon="reachability" center /> : "Erreichbarkeit"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Stimmverhalten" icon="status" center /> : "Stimmverhalten"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Letzte Änderung am" icon="date" center /> : "Letzte Änderung am"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Stimmabgabe" icon="vote" center /> : "Stimmabgabe"}</th>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Kommentar" icon="comment" /> : "Kommentar"}</th>
                    <th className={isFrameworkV2 ? "resolutions-v2-actions-head" : undefined} style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Aktion" icon="action" center /> : "Aktion"}</th>
                  </tr>
                </thead>
                <tbody>
                  {voteTopSpacerHeight > 0 ? (
                    <tr aria-hidden="true">
                      <td colSpan={8} style={{ height: voteTopSpacerHeight, padding: 0, border: 0 }} />
                    </tr>
                  ) : null}
                  {visibleRecipientRowsStimmen.map((r) => {
                    const selectedVote = draftVotes[r.assignmentId] ?? r.voteValue;
                    const selectedComment = draftComments[r.assignmentId] ?? r.comment ?? "";
                    const hasExistingVote = !!r.voteReceivedAt || r.voteValue !== "offen";
                    const reachabilityBadge = getReachabilityBadge(r.erreichbarkeit, r.temporaryAnalog);
                    return (
                      <tr key={r.assignmentId}>
                        <td style={{ textAlign: "center" }}>{formatUnitNumberForResult(r.einheitsnummer)}</td>
                        <td>{r.eigentuemerName}</td>
                        <td style={{ textAlign: "center" }}><span className={`badge ${reachabilityBadge.className}`}>{reachabilityBadge.label}</span></td>
                        <td style={{ textAlign: "center" }}><span className={`badge ${r.voteValue === "ja" ? "vote-ja" : r.voteValue === "nein" ? "vote-nein" : r.voteValue === "enthaltung" ? "vote-enthaltung" : "vote-offen"}`}>{formatVoteValue(r.voteValue)}</span></td>
                        <td style={{ textAlign: "center" }}>{formatDateTimeDE(r.voteReceivedAt)}</td>
                        <td style={{ textAlign: "center" }}>
                          <div className="vote-choice-group" role="group" aria-label="Stimmabgabe auswählen" style={{ justifyContent: "center" }}>
                            {(["ja", "nein", "enthaltung"] as VoteValue[]).map((value) => (
                              <button
                                key={value}
                                type="button"
                                className={`vote-choice-btn ${selectedVote === value ? "is-active" : ""} vote-choice-${value}`}
                                disabled={!canCaptureVotes}
                                onClick={() => setDraftVotes((old) => ({ ...old, [r.assignmentId]: value }))}
                              >
                                {value === "enthaltung" ? "Enth." : formatVoteValue(value)}
                              </button>
                            ))}
                          </div>
                        </td>
                        <td style={{ minWidth: 240, maxWidth: 320 }}>
                          <input className="table-field"
                            placeholder="Kommentar"
                            value={selectedComment}
                            onChange={(e) => setDraftComments((old) => ({ ...old, [r.assignmentId]: e.target.value }))}
                            disabled={!canCaptureVotes}
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {isFrameworkV2 ? (
                            <div
                              className="resolutions-v2-action-wrap"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="resolutions-v2-action-trigger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (voteOpenActionMenuId === r.assignmentId) {
                                    setVoteOpenActionMenuId(null);
                                    return;
                                  }
                                  openFloatingActionMenu(r.assignmentId, e.currentTarget, setVoteOpenActionMenuId, 58);
                                }}
                                aria-label="Aktionen öffnen"
                                aria-expanded={voteOpenActionMenuId === r.assignmentId}
                              >
                                &#8942;
                              </button>
                              {voteOpenActionMenuId === r.assignmentId && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                                <div
                                  className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed"
                                  style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }}
                                  onMouseEnter={() => setIsVoteActionMenuHovered(true)}
                                  onMouseLeave={() => setIsVoteActionMenuHovered(false)}
                                >
                                  <button
                                    type="button"
                                    disabled={!canCaptureVotes || voteMutation.isPending || selectedVote === "offen"}
                                    onClick={() => {
                                      const payload = {
                                        assignmentId: r.assignmentId,
                                        voteValue: selectedVote,
                                        comment: selectedComment.trim() ? selectedComment.trim() : undefined
                                      };

                                      if (!hasExistingVote) {
                                        voteMutation.mutate(payload);
                                        setVoteOpenActionMenuId(null);
                                        return;
                                      }

                                      setVoteChangeConfirm({
                                        assignmentId: payload.assignmentId,
                                        eigentuemerLabel: r.eigentuemerName,
                                        code: String(Math.floor(1000 + Math.random() * 9000)),
                                        input: ["", "", "", ""],
                                        error: "",
                                        voteValue: payload.voteValue,
                                        comment: payload.comment
                                      });
                                      setVoteOpenActionMenuId(null);
                                    }}
                                  >
                                    {hasExistingVote ? "Ändern" : "Speichern"}
                                  </button>
                                </div>,
                                document.body
                              ) : null}
                            </div>
                          ) : (
                            <button
                              className={hasExistingVote ? "vote-action-btn vote-change-btn" : "vote-action-btn vote-save-btn"}
                              disabled={!canCaptureVotes || voteMutation.isPending || selectedVote === "offen"}
                              onClick={() => {
                                const payload = {
                                  assignmentId: r.assignmentId,
                                  voteValue: selectedVote,
                                  comment: selectedComment.trim() ? selectedComment.trim() : undefined
                                };

                                if (!hasExistingVote) {
                                  voteMutation.mutate(payload);
                                  return;
                                }

                                setVoteChangeConfirm({
                                  assignmentId: payload.assignmentId,
                                  eigentuemerLabel: r.eigentuemerName,
                                  code: String(Math.floor(1000 + Math.random() * 9000)),
                                  input: ["", "", "", ""],
                                  error: "",
                                  voteValue: payload.voteValue,
                                  comment: payload.comment
                                });
                              }}
                            >
                              {hasExistingVote ? "Ändern" : "Speichern"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {voteBottomSpacerHeight > 0 ? (
                    <tr aria-hidden="true">
                      <td colSpan={8} style={{ height: voteBottomSpacerHeight, padding: 0, border: 0 }} />
                    </tr>
                  ) : null}
                  {!filteredRecipientRowsStimmen.length ? <tr><td colSpan={8}>Keine Einträge gefunden.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}

      {tab === "versand" ? (
        <>
          <ShippingProcessPanel
            resolutionId={id as string}
            hasDigitalRecipients={digitalCount > 0}
            hasAnalogRecipients={analogCount > 0}
            digitalRecipientCount={digitalCount}
            analogRecipientCount={analogCount}
          />
          <section className="panel">
            <h3 style={{ marginTop: 0 }}>Trigger-Protokoll (mit Zustellnachweis)</h3>
            <div className={isFrameworkV2 ? "resolution-detail-v2-table-wrap" : undefined} style={isFrameworkV2 ? { maxHeight: 360 } : { overflowX: "auto", maxHeight: 360 }}>
              <table className={isFrameworkV2 ? "resolution-detail-v2-table resolution-detail-shipping-v2-table resolution-detail-shipping-trigger-log-v2-table" : undefined}>
                <thead>
                  <tr>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Zeit" icon="date" /> : "Zeit"}</th>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Trigger" icon="status" /> : "Trigger"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Kanal" icon="mail" center /> : "Kanal"}</th>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Status" icon="status" /> : "Status"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="HTTP" icon="key" center /> : "HTTP"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Endpoint" icon="link" center /> : "Endpoint"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Versuche" icon="key" center /> : "Versuche"}</th>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Nachricht" icon="comment" /> : "Nachricht"}</th>
                  </tr>
                </thead>
                <tbody>
                  {triggerNotificationLogRows.map((row) => (
                    <tr key={`trigger-log-${row.id}`}>
                      <td>{formatDateTimeDE(row.deliveredAt || row.updatedAt || row.createdAt)}</td>
                      <td>{formatTriggerEventType(row.eventType)}</td>
                      <td style={{ textAlign: "center" }}>
                        <span className="badge erreichbar-digital">
                          {formatTriggerChannel(row.channel)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${row.status === "sent" ? "aktiv" : row.status === "failed" ? "inaktiv" : "neutral"}`}>
                          {formatTriggerDeliveryStatus(row.status)}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {typeof row.deliveryHttpStatus === "number"
                          ? row.deliveryHttpStatus
                          : row.status === "sent" && row.channel === "facilioo"
                            ? "ohne Nachweis"
                            : "-"}
                      </td>
                      <td style={{ textAlign: "center" }}><code>{row.deliveryEndpoint || "-"}</code></td>
                      <td style={{ textAlign: "center" }}>{row.attempts} / {row.maxAttempts}</td>
                      <td>{row.lastError || row.messageId || "-"}</td>
                    </tr>
                  ))}
                  {!triggerNotificationLogRows.length ? <tr><td colSpan={8}>Keine Trigger-Einträge vorhanden.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel">
            <div className={isFrameworkV2 ? "resolution-detail-v2-table-wrap" : undefined} style={isFrameworkV2 ? undefined : { overflowX: "auto" }}>
              <table className={isFrameworkV2 ? "resolution-detail-v2-table resolution-detail-shipping-v2-table resolution-detail-shipping-status-v2-table" : undefined}>
                <thead>
                  <tr>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Versandstatus" icon="status" /> : "Versandstatus"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Status" icon="status" center /> : "Status"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Versendet am" icon="date" center /> : "Versendet am"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Vorgemerkter Termin" icon="date" center /> : "Vorgemerkter Termin"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Dokumente (ZIP)" icon="document" center /> : "Dokumente (ZIP)"}</th>
                  </tr>
                </thead>
                <tbody>
                  {dispatchRows.map((row) => (
                    <tr key={row.eventType}>
                      <td>{row.label}</td>
                      <td style={{ textAlign: "center" }}>
                        <span className={`badge ${getDispatchUiStatusBadgeClass(row.status)}`}>{formatDispatchUiStatus(row.status)}</span>
                      </td>
                      <td style={{ textAlign: "center" }}>{formatDateTimeDE(row.timestamp)}</td>
                      <td style={{ textAlign: "center" }}>{formatDateDE(row.plannedDate)}</td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          type="button"
                          onClick={() => void handleDownloadDispatchZip(row.eventType)}
                          disabled={row.generatedPdfCount <= 0}
                          title={
                            row.generatedPdfCount > 0
                              ? `ZIP herunterladen (${row.generatedPdfCount} PDF)`
                              : "Keine erzeugten PDF-Dokumente für diesen Versandtyp vorhanden"
                          }
                        >
                          ZIP laden
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel">
            <section className={isFrameworkV2 ? "resolutions-v2-filterbar resolution-detail-v2-filterbar" : "filterbar"} style={isFrameworkV2 ? undefined : { marginBottom: 10 }}>
              <div className={isFrameworkV2 ? "resolutions-v2-filter-fields" : undefined}>
                <label className={isFrameworkV2 ? "resolutions-v2-filter-input resolution-detail-v2-filter-input" : undefined}>
                  {isFrameworkV2 ? (
                    <span className="resolutions-v2-filter-icon" aria-hidden="true"><ResolutionDetailTableIcon name="search" /></span>
                  ) : null}
                  <input className={isFrameworkV2 ? undefined : "table-field"}
                    placeholder="Suche..."
                    value={searchVersand}
                    onChange={(e) => setSearchVersand(e.target.value)}
                    style={isFrameworkV2 ? undefined : { minWidth: 360 }}
                  />
                </label>
              </div>
            </section>
            {isFrameworkV2 ? (
              <div className="resolutions-v2-row-count resolution-detail-v2-row-count" aria-hidden="true">{"\u00a0"}</div>
            ) : null}

            <div className={isFrameworkV2 ? "resolution-detail-v2-table-wrap" : undefined} style={isFrameworkV2 ? { maxHeight: 460 } : { overflowX: "auto", maxHeight: 460 }}>
              <table className={isFrameworkV2 ? "resolution-detail-v2-table resolution-detail-shipping-v2-table resolution-detail-shipping-recipients-v2-table" : undefined}>
                <thead>
                  <tr>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Empfänger" icon="ownerName" /> : "Empfänger"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Einheit" icon="unit" center /> : "Einheit"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Erreichbarkeit" icon="reachability" center /> : "Erreichbarkeit"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Einladung" icon="mail" center /> : "Einladung"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Erinnerung 1" icon="mail" center /> : "Erinnerung 1"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Erinnerung 2" icon="mail" center /> : "Erinnerung 2"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Ergebnisversand" icon="mail" center /> : "Ergebnisversand"}</th>
                  </tr>
                </thead>
                <tbody>
                  {versandEmpfaengerRows.map((r) => {
                    const reachabilityBadge = getReachabilityBadge(r.erreichbarkeit, r.temporaryAnalog);
                    return (
                      <tr key={`versand-${r.eigentuemerId}`}>
                        <td>{r.eigentuemerName}</td>
                        <td style={{ textAlign: "center" }}>{r.einheitenLabel}</td>
                        <td style={{ textAlign: "center" }}><span className={`badge ${reachabilityBadge.className}`}>{reachabilityBadge.label}</span></td>
                        <td style={{ textAlign: "center" }}>{renderDispatchBadge("einladung_versendet", r.erreichbarkeit, r.eigentuemerId)}</td>
                        <td style={{ textAlign: "center" }}>{renderDispatchBadge("erinnerung_1_versendet", r.erreichbarkeit, r.eigentuemerId)}</td>
                        <td style={{ textAlign: "center" }}>{renderDispatchBadge("erinnerung_2_versendet", r.erreichbarkeit, r.eigentuemerId)}</td>
                        <td style={{ textAlign: "center" }}>{renderDispatchBadge("beschlussergebnis_versendet", r.erreichbarkeit, r.eigentuemerId)}</td>
                      </tr>
                    );
                  })}
                  {!versandEmpfaengerRows.length ? <tr><td colSpan={7}>Keine Einträge gefunden.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}

      {tab === "ergebnis" ? (
        <section className="panel">
          <section className="result-top-layout" style={{ marginBottom: 12 }}>
            <section className="panel-inset result-chart-box">
              <div className="result-chart-header">
                <button type="button" onClick={openVoteLogModal}>Logdatei öffnen</button>
              </div>
              <VoteDonutChart
                ja={Number(resultQuery.data?.stimmen.ja ?? 0)}
                nein={Number(resultQuery.data?.stimmen.nein ?? 0)}
                enthaltung={Number(resultQuery.data?.stimmen.enthaltung ?? 0)}
              />
            </section>
            <section className="result-kpi-grid">
              <div className="kpi object-overview-kpi-v2 result-kpi-card-v2 result-kpi-card-v2--return-bar">
                <span className="object-overview-kpi-icon" aria-hidden="true"><ReturnRateIcon /></span>
                <div className="object-overview-kpi-copy result-return-kpi-copy-v2">
                  <span>Rücklaufquote</span>
                  <div className="result-return-kpi-body-v2">
                    <div className="result-return-kpi-track-v2" style={resultReturnBarStyle} aria-hidden="true">
                      <span className="result-return-kpi-segment-v2 result-return-kpi-segment-v2--submitted" />
                      <span className="result-return-kpi-segment-v2 result-return-kpi-segment-v2--open" />
                    </div>
                    <div className="result-return-kpi-legend-v2">
                      <div className="reachability-line resolution-detail-legend-line resolution-detail-legend-line--green">
                        <span className="dot dot-digital" /> <strong>Abgegeben</strong>
                        <small>{resultReturnSubmitted} Stimmen</small>
                        <b>{formatShare(resultReturnSubmitted, resultReturnTotal)} %</b>
                      </div>
                      <div className="reachability-line resolution-detail-legend-line resolution-detail-legend-line--neutral">
                        <span className="dot dot-neutral" /> <strong>Offen</strong>
                        <small>{resultReturnOpen} Stimmen</small>
                        <b>{formatShare(resultReturnOpen, resultReturnTotal)} %</b>
                      </div>
                      <div className="reachability-total result-return-kpi-total-v2">
                        <span>Gesamt</span>
                        <strong>{resultReturnTotal} Stimmen</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={`kpi object-overview-kpi-v2 result-kpi-card-v2 result-kpi-card-v2--quorum result-kpi-card-v2--quorum-${resultQuorumFulfilled ? "ok" : "fail"}`}>
                <span className="result-kpi-quorum-icon" aria-hidden="true">
                  <QuorumOutcomeIcon fulfilled={resultQuorumFulfilled} />
                </span>
                <div className="object-overview-kpi-copy">
                  <span>Quorum erfüllt</span>
                  <strong>{resultQuorumFulfilled ? "Ja" : "Nein"}</strong>
                </div>
              </div>
              <div className={`kpi object-overview-kpi-v2 result-kpi-card-v2 result-kpi-card-v2--compact result-kpi-card-v2--outcome result-kpi-card-v2--${resultKpiTone}`}>
                <span className="result-kpi-outcome-icon" aria-hidden="true">
                  <ResultOutcomeIcon rejected={resultKpiTone === "fail"} />
                </span>
                <div className="object-overview-kpi-copy">
                  <span>{resultKpiTitle}</span>
                  <strong>{resultKpiValue}</strong>
                {showResultBreakEvenNote ? (
                  <small className="result-kpi-note-v2">
                    Aus den offenen Stimmen kann sich keine Veränderung des Gesamtergebnisses mehr ergeben.
                  </small>
                ) : null}
                </div>
                <small className="result-kpi-calculation-note-v2">
                  Berechnung auf Basis der Stimmrechtsart {formatStimmrechtsart(resolution.stimmrechtsart)} und Quorum {formatQuorum(resolution.quorum)}.
                </small>
              </div>
            </section>
          </section>
          <section className="panel result-votes-launch-panel" style={{ marginBottom: 10 }}>
            <div className="result-votes-launch-copy">
              <h3>Einzelstimmen</h3>
              <p>{recipientRows.length} stimmberechtigte Einheiten. Die Liste wird im Overlayfenster geöffnet.</p>
            </div>
            <button
              type="button"
              className={isFrameworkV2 ? "resolution-detail-v2-secondary-btn" : undefined}
              onClick={() => setIsResultVotesModalOpen(true)}
            >
              Einzelstimmen öffnen
            </button>
          </section>
          <section className="panel-inset result-visibility-card v2-info-banner" style={{ marginBottom: 10 }}>
            <div className="result-visibility-header">
              <div className="result-visibility-copy">
                <div className="result-visibility-title">Anzeige Einzelstimmabgabe</div>
                <div className="result-visibility-text">
                  Aktiv: Auf der Eigentümer-Ergebnisseite wird die Liste mit Einzelstimmen angezeigt. Aus: Es wird nur das Gesamtergebnis angezeigt.
                </div>
              </div>
              <label className="result-visibility-switch" aria-label="Einzelstimmabgabe auf Eigentümer-Ergebnisseite anzeigen">
                <input
                  className="result-visibility-input"
                  type="checkbox"
                  checked={ownerEinzelstimmabgabeSichtbar}
                  disabled={ownerResultVisibilityMutation.isPending}
                  onChange={async (e) => {
                    const next = e.target.checked;
                    setOwnerEinzelstimmabgabeSichtbar(next);
                    await ownerResultVisibilityMutation.mutateAsync(next);
                  }}
                />
                <span className={`result-visibility-track ${ownerEinzelstimmabgabeSichtbar ? "is-on" : ""}`} aria-hidden="true">
                  <span className="result-visibility-thumb" />
                </span>
                <span className={`badge ${ownerEinzelstimmabgabeSichtbar ? "aktiv" : "ergebnis-neutral"}`}>
                  {ownerEinzelstimmabgabeSichtbar ? "Sichtbar" : "Ausgeblendet"}
                </span>
              </label>
            </div>
          </section>

        </section>
      ) : null}

      {tab === "dokumente" ? (
        <section className="panel" style={{ display: "grid", gap: 12 }}>
          <section className="panel" style={{ margin: 0 }}>
            <div
              className={`resolution-document-dropzone${documentDropActive ? " is-drag-over" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "copy";
                setDocumentDropActive(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setDocumentDropActive(false);
              }}
              onDrop={onDocumentDrop}
            >
              <input
                ref={documentInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/png"
                style={{ display: "none" }}
                onChange={(e) => mergeDocumentFiles(Array.from(e.target.files ?? []), true)}
              />
              <div className="resolution-document-dropzone-text">Dateien hier hineinziehen oder über den Button auswählen.</div>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
              <button type="button" onClick={() => documentInputRef.current?.click()} disabled={documentUploadMutation.isPending}>
                Dateien auswählen
              </button>
              <button type="button" onClick={() => documentUploadMutation.mutate()} disabled={!documentFiles.length || documentUploadMutation.isPending}>
                {documentUploadMutation.isPending ? "Lade hoch..." : "Dokumente hochladen"}
              </button>
              <button
                type="button"
                onClick={() => setSelectedDocumentIds(documentRows.map((doc) => doc.id))}
                disabled={!documentRows.length || uploadDocumentsToFaciliooMutation.isPending}
              >
                Alle markieren
              </button>
              <button
                type="button"
                onClick={() => setSelectedDocumentIds([])}
                disabled={!selectedDocumentIds.length || uploadDocumentsToFaciliooMutation.isPending}
              >
                Alle demarkieren
              </button>
              <button
                type="button"
                onClick={() => uploadDocumentsToFaciliooMutation.mutate()}
                disabled={!resolution?.facilioo_vorgangsnummer || !selectedDocumentIds.length || uploadDocumentsToFaciliooMutation.isPending}
              >
                {uploadDocumentsToFaciliooMutation.isPending ? "Übertrage..." : "Markierte zu Facilioo übertragen"}
              </button>
              {documentFiles.length ? (
                <span style={{ color: "var(--text-soft)", fontSize: 12 }}>
                  {`${documentFiles.length} neue lokale Datei(en) zum Hochladen ausgewählt`}
                </span>
              ) : null}
              <span style={{ color: "var(--text-soft)", fontSize: 12 }}>
                {selectedDocumentIds.length
                  ? `${selectedDocumentIds.length} Dokument(e) für Facilioo markiert`
                  : `${documentRows.length} Dokument(e) in der Liste`}
              </span>
            </div>
            <div style={{ marginTop: 6, color: "var(--text-soft)", fontSize: 12 }}>
              Erlaubte Formate: PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG. Maximal 20 Dateien pro Upload, maximal 20 MB je Datei.
            </div>
            {!resolution?.facilioo_vorgangsnummer ? (
              <div style={{ marginTop: 6, color: "var(--text-soft)", fontSize: 12 }}>
                Für die Übertragung zu Facilioo muss am Beschluss eine Facilioo Vorgangsnummer hinterlegt sein.
              </div>
            ) : null}
            {documentInfo ? <div style={{ marginTop: 8, color: "var(--text-soft)", fontSize: 12 }}>{documentInfo}</div> : null}
            {documentError ? <div className="error-text" style={{ marginTop: 8 }}>{documentError}</div> : null}
          </section>

          <section className={isFrameworkV2 ? "panel resolution-detail-v2-list-panel resolution-detail-documents-v2-panel" : "panel"} style={isFrameworkV2 ? undefined : { margin: 0 }}>
            {isFrameworkV2 ? (
              <div className="resolutions-v2-row-count resolution-detail-v2-row-count">
                Dokumente: {filteredDocumentRows.length}{filteredDocumentRows.length !== documentRows.length ? ` / ${documentRows.length}` : ""}
              </div>
            ) : null}
            <section className={isFrameworkV2 ? "resolutions-v2-filterbar resolution-detail-v2-filterbar" : "filterbar"} style={isFrameworkV2 ? undefined : { marginBottom: 10 }}>
              <div className={isFrameworkV2 ? "resolutions-v2-filter-fields" : undefined}>
                <label className={isFrameworkV2 ? "resolutions-v2-filter-input resolution-detail-v2-filter-input" : undefined}>
                  {isFrameworkV2 ? (
                    <span className="resolutions-v2-filter-icon" aria-hidden="true"><ResolutionDetailTableIcon name="search" /></span>
                  ) : null}
                  <input
                    className={isFrameworkV2 ? undefined : "table-field"}
                    placeholder="Suche..."
                    value={searchDokumente}
                    onChange={(e) => setSearchDokumente(e.target.value)}
                    style={isFrameworkV2 ? undefined : { minWidth: 320 }}
                  />
                </label>
              </div>
            </section>
            <div className={isFrameworkV2 ? "resolution-detail-v2-table-wrap" : undefined} style={isFrameworkV2 ? undefined : { overflowX: "auto" }}>
              <table className={isFrameworkV2 ? "resolution-detail-v2-table resolution-detail-documents-v2-table" : undefined}>
                <thead>
                  <tr>
                    <th style={{ width: 78, textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Aktiv" icon="select" center /> : "Aktiv"}</th>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Dateiname" icon="document" /> : "Dateiname"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Größe" icon="size" center /> : "Größe"}</th>
                    <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Hochgeladen am" icon="date" center /> : "Hochgeladen am"}</th>
                    <th>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Hochgeladen von" icon="owner" /> : "Hochgeladen von"}</th>
                    <th style={{ textAlign: "center" }} aria-label="Aktion">{isFrameworkV2 ? <ResolutionDetailTableHeader label="" icon="action" center /> : "Aktion"}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocumentRows.map((doc) => (
                    <tr key={doc.id}>
                      <td style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={selectedDocumentIds.includes(doc.id)}
                          onChange={(e) =>
                            setSelectedDocumentIds((prev) =>
                              e.target.checked ? Array.from(new Set([...prev, doc.id])) : prev.filter((id) => id !== doc.id)
                            )
                          }
                        />
                      </td>
                      <td>{doc.originalName}</td>
                      <td style={{ textAlign: "center" }}>{(doc.size / 1024).toFixed(1)} KB</td>
                      <td style={{ textAlign: "center" }}>{formatDateTimeDE(doc.uploadedAt)}</td>
                      <td>{doc.uploadedByName}</td>
                      <td style={isFrameworkV2 ? { textAlign: "center" } : { textAlign: "center", display: "flex", gap: 8, justifyContent: "center" }}>
                        {isFrameworkV2 ? (
                          <div className="resolutions-v2-action-wrap" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="resolutions-v2-action-trigger"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (documentOpenActionMenuId === doc.id) {
                                  setDocumentOpenActionMenuId(null);
                                  return;
                                }
                                openFloatingActionMenu(doc.id, e.currentTarget, setDocumentOpenActionMenuId, canManageMasterDataActions ? 96 : 52);
                              }}
                              aria-label="Aktionen öffnen"
                              aria-expanded={documentOpenActionMenuId === doc.id}
                            >
                              &#8942;
                            </button>
                            {documentOpenActionMenuId === doc.id && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                              <div
                                className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed"
                                style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }}
                                onMouseEnter={() => setIsDocumentActionMenuHovered(true)}
                                onMouseLeave={() => setIsDocumentActionMenuHovered(false)}
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPreviewDocument(doc);
                                    setDocumentOpenActionMenuId(null);
                                  }}
                                >
                                  Öffnen
                                </button>
                                {canManageMasterDataActions ? (
                                  <button
                                    type="button"
                                    disabled={documentDeleteMutation.isPending}
                                    onClick={() => {
                                      setDocumentDeleteConfirm({
                                        documentId: doc.id,
                                        documentName: doc.originalName,
                                        code: String(Math.floor(1000 + Math.random() * 9000)),
                                        input: ["", "", "", ""],
                                        error: ""
                                      });
                                      setDocumentOpenActionMenuId(null);
                                    }}
                                  >
                                    Löschen
                                  </button>
                                ) : null}
                              </div>,
                              document.body
                            ) : null}
                          </div>
                        ) : (
                          <>
                            <button type="button" onClick={() => setPreviewDocument(doc)}>Öffnen</button>
                            {canManageMasterDataActions ? (
                              <button
                                type="button"
                                disabled={documentDeleteMutation.isPending}
                                onClick={() => {
                                  setDocumentDeleteConfirm({
                                    documentId: doc.id,
                                    documentName: doc.originalName,
                                    code: String(Math.floor(1000 + Math.random() * 9000)),
                                    input: ["", "", "", ""],
                                    error: ""
                                  });
                                }}
                              >
                                Löschen
                              </button>
                            ) : null}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!filteredDocumentRows.length ? <tr><td colSpan={6}>{documentRows.length ? "Keine Dokumente gefunden." : "Für diesen Umlaufbeschluss sind aktuell keine Dokumente hinterlegt."}</td></tr> : null}
                </tbody>
              </table>
            </div>
          </section>

          {previewDocument ? (
            <div className="modal-backdrop">
              <section className="panel modal-card" style={{ width: "min(1400px, 98vw)", maxWidth: 1400, maxHeight: "94vh", overflow: "auto" }}>
                <h3 style={{ marginTop: 0, marginBottom: 8 }}>{previewDocument.originalName}</h3>
                <div style={{ marginBottom: 10 }}>
                  {isPreviewableDocument(previewDocument) ? (
                    <iframe title={previewDocument.originalName} src={previewDocument.fileUrl} style={{ width: "100%", height: "80vh", border: "1px solid var(--line)", borderRadius: 6 }} />
                  ) : (
                    <div style={{ color: "var(--text-soft)" }}>
                      Für diesen Dateityp ist keine Browser-Vorschau verfügbar.
                    </div>
                  )}
                </div>
                <div className="form-actions">
                  <a href={previewDocument.fileUrl} download={previewDocument.originalName}><button type="button">Download</button></a>
                  <button type="button" onClick={() => setPreviewDocument(null)}>Schließen</button>
                </div>
              </section>
            </div>
          ) : null}
        </section>
      ) : null}

      {tab === "facilioo_dokumente" ? (
        <section className="panel" style={{ display: "grid", gap: 12 }}>
          {!resolution?.facilioo_vorgangsnummer ? (
            <div style={{ color: "var(--text-soft)" }}>
              Für diesen Umlaufbeschluss ist keine Facilioo Vorgangsnummer hinterlegt.
            </div>
          ) : (
            <>
              <section className="panel" style={{ margin: 0 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={() => setSelectedFaciliooAttachmentKeys(faciliooAttachmentRows.map((row) => `${row.feedId}:${row.attachmentId}`))}
                    disabled={!faciliooAttachmentRows.length || importFaciliooAttachmentsMutation.isPending}
                  >
                    Alle markieren
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedFaciliooAttachmentKeys([])}
                    disabled={!selectedFaciliooAttachmentKeys.length || importFaciliooAttachmentsMutation.isPending}
                  >
                    Alle demarkieren
                  </button>
                  <button
                    type="button"
                    onClick={() => importFaciliooAttachmentsMutation.mutate("selected")}
                    disabled={!selectedFaciliooAttachmentKeys.length || importFaciliooAttachmentsMutation.isPending}
                  >
                    {importFaciliooAttachmentsMutation.isPending ? "Importiere..." : "Ausgewählte importieren"}
                  </button>
                  <button
                    type="button"
                    onClick={() => importFaciliooAttachmentsMutation.mutate("all")}
                    disabled={!faciliooAttachmentRows.length || importFaciliooAttachmentsMutation.isPending}
                  >
                    {importFaciliooAttachmentsMutation.isPending ? "Lade neu..." : "Alle neu aus Facilioo laden"}
                  </button>
                  <span style={{ color: "var(--text-soft)", fontSize: 12 }}>
                    {selectedFaciliooAttachmentKeys.length} ausgewählt
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowFileTypesDebug((prev) => !prev)}
                    disabled={importFaciliooAttachmentsMutation.isPending}
                  >
                    {showFileTypesDebug ? "File-Typen Debug ausblenden" : "File-Typen Debug anzeigen"}
                  </button>
                </div>
                {faciliooDocumentError ? <div className="error-text" style={{ marginTop: 8 }}>{faciliooDocumentError}</div> : null}
              </section>

              {showFileTypesDebug ? (
                <section className="panel" style={{ margin: 0 }}>
                  <h4 style={{ marginTop: 0, marginBottom: 8 }}>Debug: Facilioo File-Typen</h4>
                  <div style={{ color: "var(--text-soft)", fontSize: 12, marginBottom: 8 }}>
                    Gesamt: {faciliooFileTypesDebugQuery.data?.total ?? 0}
                  </div>
                  <div style={{ overflowX: "auto", maxHeight: 320 }}>
                    <table>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "center" }}>ID</th>
                          <th style={{ textAlign: "center" }}>Endung</th>
                          <th>Beschreibung</th>
                          <th>MIME</th>
                        </tr>
                      </thead>
                      <tbody>
                        {faciliooFileTypesDebugQuery.isLoading ? (
                          <tr><td colSpan={4}>File-Typen werden geladen ...</td></tr>
                        ) : null}
                        {faciliooFileTypeRows.map((row) => (
                          <tr key={row.id}>
                            <td style={{ textAlign: "center" }}>{row.id}</td>
                            <td style={{ textAlign: "center" }}>{row.fileExtension ? row.fileExtension.toUpperCase() : "-"}</td>
                            <td>{row.description || "-"}</td>
                            <td>{row.mimeType || "-"}</td>
                          </tr>
                        ))}
                        {!faciliooFileTypesDebugQuery.isLoading && !faciliooFileTypeRows.length ? (
                          <tr><td colSpan={4}>Keine File-Typen gefunden.</td></tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}

              <section className={isFrameworkV2 ? "panel resolution-detail-v2-list-panel resolution-detail-documents-v2-panel" : "panel"} style={isFrameworkV2 ? undefined : { margin: 0 }}>
                {isFrameworkV2 ? (
                  <div className="resolutions-v2-row-count resolution-detail-v2-row-count">
                    Facilioo-Dokumente: {filteredFaciliooAttachmentRows.length}{filteredFaciliooAttachmentRows.length !== faciliooAttachmentRows.length ? ` / ${faciliooAttachmentRows.length}` : ""}
                  </div>
                ) : null}
                <section className={isFrameworkV2 ? "resolutions-v2-filterbar resolution-detail-v2-filterbar" : "filterbar"} style={isFrameworkV2 ? undefined : { marginBottom: 10 }}>
                  <div className={isFrameworkV2 ? "resolutions-v2-filter-fields" : undefined}>
                    <label className={isFrameworkV2 ? "resolutions-v2-filter-input resolution-detail-v2-filter-input" : undefined}>
                      {isFrameworkV2 ? (
                        <span className="resolutions-v2-filter-icon" aria-hidden="true"><ResolutionDetailTableIcon name="search" /></span>
                      ) : null}
                      <input
                        className={isFrameworkV2 ? undefined : "table-field"}
                        placeholder="Suche..."
                        value={searchFaciliooDokumente}
                        onChange={(e) => setSearchFaciliooDokumente(e.target.value)}
                        style={isFrameworkV2 ? undefined : { minWidth: 320 }}
                      />
                    </label>
                  </div>
                </section>
                <div className={isFrameworkV2 ? "resolution-detail-v2-table-wrap" : undefined} style={isFrameworkV2 ? undefined : { overflowX: "auto" }}>
                  <table className={isFrameworkV2 ? "resolution-detail-v2-table resolution-detail-facilioo-documents-v2-table" : undefined}>
                    <thead>
                      <tr>
                        <th style={{ width: 78, textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Aktiv" icon="select" center /> : "Aktiv"}</th>
                        <th>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Dateiname" icon="document" /> : "Dateiname"}</th>
                        <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Dateiart" icon="type" center /> : "Dateiart"}</th>
                        <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Größe" icon="size" center /> : "Größe"}</th>
                        <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Geändert am" icon="date" center /> : "Geändert am"}</th>
                        <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionDetailTableHeader label="Feed-ID" icon="key" center /> : "Feed-ID"}</th>
                        <th style={{ textAlign: "center" }} aria-label="Aktion">{isFrameworkV2 ? <ResolutionDetailTableHeader label="" icon="action" center /> : "Aktion"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {faciliooAttachmentsQuery.isLoading ? (
                        <tr><td colSpan={7}>Facilioo-Dokumente werden geladen ...</td></tr>
                      ) : null}
                      {filteredFaciliooAttachmentRows.map((row) => {
                        const key = `${row.feedId}:${row.attachmentId}`;
                        const checked = selectedFaciliooAttachmentKeys.includes(key);
                        const fileSizeBytes = Number.isFinite(Number(row.filesizeKb)) ? Number(row.filesizeKb) * 1024 : 0;
                        return (
                          <tr key={key}>
                            <td style={{ textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) =>
                                  setSelectedFaciliooAttachmentKeys((prev) =>
                                    e.target.checked
                                      ? Array.from(new Set([...prev, key]))
                                      : prev.filter((entry) => entry !== key)
                                  )
                                }
                              />
                            </td>
                            <td>{row.originalFilename || `Anhang ${row.attachmentId}`}</td>
                            <td style={{ textAlign: "center" }}>{resolveFileTypeLabel(row)}</td>
                            <td style={{ textAlign: "center" }}>{formatFileSize(fileSizeBytes)}</td>
                            <td style={{ textAlign: "center" }}>{formatDateTimeDE(row.lastModifiedAt ?? row.createdAt ?? undefined)}</td>
                            <td style={{ textAlign: "center" }}>{row.feedId}</td>
                            <td style={{ textAlign: "center" }}>
                              {isFrameworkV2 ? (
                                <div className="resolutions-v2-action-wrap" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    className="resolutions-v2-action-trigger"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (faciliooOpenActionMenuId === key) {
                                        setFaciliooOpenActionMenuId(null);
                                        return;
                                      }
                                      openFloatingActionMenu(key, e.currentTarget, setFaciliooOpenActionMenuId, 52);
                                    }}
                                    aria-label="Aktionen öffnen"
                                    aria-expanded={faciliooOpenActionMenuId === key}
                                  >
                                    &#8942;
                                  </button>
                                  {faciliooOpenActionMenuId === key && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                                    <div
                                      className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed"
                                      style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }}
                                      onMouseEnter={() => setIsFaciliooActionMenuHovered(true)}
                                      onMouseLeave={() => setIsFaciliooActionMenuHovered(false)}
                                    >
                                      <button
                                        type="button"
                                        onClick={() => {
                                          previewFaciliooAttachmentMutation.mutate(row);
                                          setFaciliooOpenActionMenuId(null);
                                        }}
                                        disabled={previewFaciliooAttachmentMutation.isPending}
                                      >
                                        Vorschau
                                      </button>
                                    </div>,
                                    document.body
                                  ) : null}
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => previewFaciliooAttachmentMutation.mutate(row)}
                                  disabled={previewFaciliooAttachmentMutation.isPending}
                                >
                                  Vorschau
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {!faciliooAttachmentsQuery.isLoading && !filteredFaciliooAttachmentRows.length ? (
                        <tr><td colSpan={7}>{faciliooAttachmentRows.length ? "Keine Facilioo-Dokumente gefunden." : "Für diesen Facilioo-Vorgang wurden keine Anhänge gefunden."}</td></tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </section>
      ) : null}

      {previewFaciliooDocument ? (
        <div className="modal-backdrop">
          <section className="panel modal-card" style={{ width: "min(1400px, 98vw)", maxWidth: 1400, maxHeight: "94vh", overflow: "auto" }}>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>{previewFaciliooDocument.name}</h3>
            <div style={{ marginBottom: 10 }}>
              {previewFaciliooDocument.previewable ? (
                <iframe title={previewFaciliooDocument.name} src={previewFaciliooDocument.url} style={{ width: "100%", height: "80vh", border: "1px solid var(--line)", borderRadius: 6 }} />
              ) : (
                <div style={{ border: "1px solid var(--line)", borderRadius: 6, padding: 12 }}>
                  Für diesen Dateityp ist keine Browser-Vorschau verfügbar. Bitte über den Download-Button öffnen.
                </div>
              )}
            </div>
            <div className="form-actions">
              <a href={previewFaciliooDocument.url} download={previewFaciliooDocument.name}><button type="button">Download</button></a>
              <button
                type="button"
                onClick={() => {
                  if (previewFaciliooDocument.url) URL.revokeObjectURL(previewFaciliooDocument.url);
                  setPreviewFaciliooDocument(null);
                }}
              >
                Schließen
              </button>
            </div>
          </section>
        </div>
      ) : null}
      </div>
    </>
  );
};




















































































































